// src/services/firebase.js
import { getAnalytics, isSupported, logEvent } from "firebase/analytics";
// Añade esto con las otras importaciones al principio
import { initializeApp } from "firebase/app";
import {
  browserLocalPersistence,
  connectAuthEmulator,
  getAuth,
  setPersistence,
} from "firebase/auth";
import {
  addDoc,
  arrayRemove, // Añadido - faltaba esta importación
  arrayUnion, // Añadido - faltaba esta importación
  CACHE_SIZE_UNLIMITED,
  collection,
  connectFirestoreEmulator,
  deleteDoc,
  doc,
  documentId,
  getCountFromServer,
  getDoc,
  getDocs,
  increment, // Añadido - faltaba esta importación
  initializeFirestore,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  startAfter,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { connectFunctionsEmulator, getFunctions } from "firebase/functions";
import {
  connectStorageEmulator,
  deleteObject,
  getDownloadURL,
  getStorage,
  ref,
  uploadBytesResumable,
} from "firebase/storage";

/**
 * Configuración de Firebase para VicDown
 * Las variables de entorno tienen prioridad sobre los valores predeterminados
 * @type {Object}
 */
const firebaseConfig = {
  apiKey:
    process.env.REACT_APP_FIREBASE_API_KEY ||
    "AIzaSyA5vt7fyTD7CvcBHagRrdtF3MXE5iVFZrk",
  authDomain:
    process.env.REACT_APP_FIREBASE_AUTH_DOMAIN ||
    "vicdown-3bd70.firebaseapp.com",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "vicdown-3bd70",
  storageBucket:
    process.env.REACT_APP_FIREBASE_STORAGE_BUCKET ||
    "vicdown-3bd70.firebasestorage.app",
  messagingSenderId:
    process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "951596810958",
  appId:
    process.env.REACT_APP_FIREBASE_APP_ID ||
    "1:951596810958:web:1ae583b170d37b77ed069f",
  measurementId:
    process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || "G-1DN2MTM7RM",
};

/**
 * Valida la configuración mínima de Firebase
 * @param {Object} config - Configuración de Firebase
 * @returns {boolean} - true si la configuración es válida
 */
const validateFirebaseConfig = (config) => {
  const requiredFields = ["apiKey", "authDomain", "projectId"];
  const missingFields = requiredFields.filter((field) => !config[field]);

  if (missingFields.length > 0) {
    console.error(
      `Firebase missing required config fields: ${missingFields.join(", ")}`,
    );
    return false;
  }

  return true;
};

/**
 * Estado de inicialización de Firebase
 * @type {Object}
 */
export const firebaseStatus = {
  initialized: false,
  services: {
    app: false,
    auth: false,
    firestore: false,
    storage: false,
    analytics: false,
  },
  error: null,
};

// Añadir estas constantes para centralizar los nombres de colecciones
export const COLLECTIONS = {
  BLOG_POSTS: "blog", // Cambiar de 'blog_posts' a 'blog'
  BLOG_POSTS_PENDING: "blog_posts_pending",
  BLOG_COMMENTS: "blog_comments",
  USERS: "users",
  DOWN_SYNDROME_INFO: "down_syndrome_info",
  FORUM_CATEGORIES: "forum_categories",
  FORUM_POSTS: "forum_posts",
  FORUM_COMMENTS: "forum_comments",
  SPECIALISTS: "specialists",
  SPECIALISTS_FLAT: "specialists_flat", // Nueva colección con estructura jerárquica
  SPECIALISTS_METADATA: "specialists_metadata", // Metadatos para filtrado
  DOWN_SYNDROME_NEWS: "down_syndrome_news", // Noticias sobre síndrome de Down
  RESOURCES: "resources",
  TESTIMONIALS: "testimonials",
  // Añadir colecciones para el chatbot
  CHAT_CONVERSATIONS: "chat_conversations",
  CHAT_MESSAGES: "chat_messages",
  SAVED_RESPONSES: "saved_responses",
  // Colecciones para recursos artísticos
  RESOURCES_ART: "resources_art",
  RESOURCES_ART_PENDING: "resources_art_pending",
  RESOURCE_CATEGORIES: "resource_categories",
  RESOURCE_DOWNLOADS: "resource_downloads",
  CONTEST_ENTRIES: "contest_entries",
  RESOURCE_COMMENTS: "resource_comments",
  USER_CREDITS: "user_credits",
  CREDIT_TRANSACTIONS: "credit_transactions",
};

// Constantes para Storage
export const STORAGE_PATHS = {
  BLOG_IMAGES: "blog_images",
  USER_AVATARS: "user_avatars",
  FORUM_ATTACHMENTS: "forum_attachments",
  RESOURCE_FILES: "resources",
  SPECIALIST_PHOTOS: "specialist_photos",
  RESOURCE_ART_FILES: "resource_art_files",
  CONTEST_UPLOADS: "contest_entries",
};

// Configuración de paginación
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 10,
  BLOG_PAGE_SIZE: 6,
  FORUM_PAGE_SIZE: 15,
  COMMENTS_PAGE_SIZE: 20,
};

// Configuración de caché
const CACHE_CONFIG = {
  SIZE_LIMIT: CACHE_SIZE_UNLIMITED,
  ENABLE_PERSISTENCE: true,
  ENABLE_MULTI_TAB: true,
};

// Inicialización con manejo de errores completo
let app;
let auth;
let db;
let storage;
let firebaseAnalytics;
let functions;
let persistenceEnabled = false;

try {
  // Validar configuración
  if (!validateFirebaseConfig(firebaseConfig)) {
    throw new Error("Invalid Firebase configuration");
  }

  // Inicializar app
  app = initializeApp(firebaseConfig);
  firebaseStatus.services.app = true;

  // Inicializar servicios principales
  try {
    auth = getAuth(app);
    // Configurar persistencia para mantener la sesión del usuario
    setPersistence(auth, browserLocalPersistence)
      .then(() => {
        console.log("Auth persistence enabled successfully");
      })
      .catch((error) => {
        console.warn("Auth persistence setup failed:", error.message);
      });
    firebaseStatus.services.auth = true;
  } catch (authError) {
    console.error("Firebase Auth initialization failed:", authError);
  }

  try {
    // Reemplaza el bloque 'let functions' que tienes
    try {
      functions = getFunctions(app);
      firebaseStatus.services.functions = true;

      // Configurar emulador en desarrollo local si es necesario
      if (process.env.REACT_APP_USE_FIREBASE_EMULATORS === "true") {
        connectFunctionsEmulator(functions, "localhost", 5001);
      }
    } catch (functionsError) {
      console.error(
        "Firebase Functions initialization failed:",
        functionsError,
      );
    }
    // Inicializar Firestore con configuración de caché desde el principio
    const firestoreSettings = {
      cache: {
        // Habilitar indexedDB siempre que estemos en un navegador
        indexedDbEnabled:
          typeof window !== "undefined" && CACHE_CONFIG.ENABLE_PERSISTENCE,
        // Configurar sincronización entre pestañas si está habilitada
        tabSynchronization: CACHE_CONFIG.ENABLE_MULTI_TAB,
      },
    };

    db = initializeFirestore(app, firestoreSettings);

    if (typeof window !== "undefined" && CACHE_CONFIG.ENABLE_PERSISTENCE) {
      console.log(
        `Firestore persistence enabled (multi-tab sync: ${CACHE_CONFIG.ENABLE_MULTI_TAB})`,
      );
      persistenceEnabled = true;
    }

    firebaseStatus.services.firestore = true;
  } catch (dbError) {
    console.error("Firebase Firestore initialization failed:", dbError);
  }

  try {
    storage = getStorage(app);
    firebaseStatus.services.storage = true;
  } catch (storageError) {
    console.error("Firebase Storage initialization failed:", storageError);
  }

  // Conectar a emuladores en desarrollo local
  if (process.env.REACT_APP_USE_FIREBASE_EMULATORS === "true") {
    if (auth) connectAuthEmulator(auth, "http://localhost:9099");
    if (db) connectFirestoreEmulator(db, "localhost", 8080);
    if (storage) connectStorageEmulator(storage, "localhost", 9199);
    console.log("Connected to Firebase emulators");
  }

  // Analytics solo en entorno de producción y con soporte
  if (process.env.NODE_ENV === "production") {
    isSupported()
      .then((supported) => {
        if (supported) {
          try {
            firebaseAnalytics = getAnalytics(app);
            firebaseStatus.services.analytics = true;

            // Registrar inicialización exitosa
            logEvent(firebaseAnalytics, "app_initialized");
          } catch (analyticsError) {
            console.warn(
              "Firebase Analytics initialization failed:",
              analyticsError,
            );
          }
        } else {
          console.warn(
            "Firebase Analytics is not supported in this environment.",
          );
        }
      })
      .catch((error) => {
        console.error("Error checking Analytics support:", error);
      });
  }

  // Marcar inicialización completa
  firebaseStatus.initialized = true;
  console.log("Firebase initialized successfully");
} catch (error) {
  console.error("Firebase initialization failed:", error);
  firebaseStatus.error = error.message;

  // Proporcionar implementaciones vacías para evitar errores de la aplicación
  if (!auth)
    auth = {
      signInWithEmailAndPassword: () =>
        Promise.reject(new Error("Firebase Auth not initialized")),
      createUserWithEmailAndPassword: () =>
        Promise.reject(new Error("Firebase Auth not initialized")),
      signOut: () => Promise.reject(new Error("Firebase Auth not initialized")),
    };

  if (!db)
    db = {
      collection: () => ({
        add: () => Promise.reject(new Error("Firestore not initialized")),
      }),
    };

  if (!storage)
    storage = {
      ref: () => ({
        put: () =>
          Promise.reject(new Error("Firebase Storage not initialized")),
      }),
    };
}

/**
 * Comprueba si Firebase está listo para usar
 * @returns {boolean} - true si Firebase está inicializado correctamente
 */
export const isFirebaseReady = () => {
  return firebaseStatus.initialized;
};

/**
 * Registra un evento en Analytics si está disponible
 * @param {string} eventName - Nombre del evento
 * @param {Object} [eventParams] - Parámetros opcionales del evento
 */
export const logAnalyticsEvent = (
  eventName,
  eventParams = {},
  anonymize = true,
) => {
  try {
    // Si firebaseAnalytics no está disponible (ej. modo desarrollo local)
    if (!firebaseAnalytics) {
      console.info(`[Analytics] Event: ${eventName}`, eventParams);
      return;
    }

    // Si se debe anonimizar, eliminamos cualquier información personal
    const cleanParams = anonymize ? sanitizeParams(eventParams) : eventParams;

    // Añadir timestamp si no está presente
    if (!cleanParams.timestamp) {
      cleanParams.timestamp = new Date().toISOString();
    }

    // Registrar el evento en Firebase Analytics
    logEvent(firebaseAnalytics, eventName, cleanParams);
  } catch (error) {
    // Evitamos que errores de analytics afecten la aplicación
    console.error(`[Analytics] Error logging event ${eventName}:`, error);
  }
};

/**
 * Elimina información potencialmente identificable de los parámetros de evento
 * @param {Object} params - Parámetros originales
 * @returns {Object} - Parámetros sanitizados
 * @private
 */
const sanitizeParams = (params) => {
  // Creamos una copia para no modificar el objeto original
  const sanitized = { ...params };

  // Lista de claves que pueden contener información personal
  const sensitiveKeys = [
    "email",
    "mail",
    "correo",
    "name",
    "nombre",
    "phone",
    "telefono",
    "address",
    "direccion",
    "id",
    "userId",
    "user_id",
    "password",
    "contraseña",
    "clave",
    "secret",
    "token",
  ];

  // Verificar cada clave y sanitizar si es necesario
  Object.keys(sanitized).forEach((key) => {
    const lowerKey = key.toLowerCase();

    // Si la clave parece contener información sensible
    if (sensitiveKeys.some((sensitive) => lowerKey.includes(sensitive))) {
      if (typeof sanitized[key] === "string") {
        // Reemplazar con un hash o valor anónimo
        sanitized[key] = "[REDACTED]";
      } else if (
        typeof sanitized[key] === "number" ||
        typeof sanitized[key] === "bigint"
      ) {
        // Para IDs numéricos, convertir a 0 o un hash
        sanitized[key] = 0;
      }
    }

    // Para casos específicos como geolocalización, reducir precisión
    if (
      lowerKey.includes("lat") ||
      lowerKey.includes("lng") ||
      lowerKey.includes("latitude") ||
      lowerKey.includes("longitude")
    ) {
      if (typeof sanitized[key] === "number") {
        // Reducir precisión a solo 1 decimal
        sanitized[key] = Math.round(sanitized[key] * 10) / 10;
      }
    }
  });

  return sanitized;
};

/**
 * Obtiene documentos paginados de una colección
 * @param {string} collectionName - Nombre de la colección
 * @param {Object} options - Opciones de paginación
 * @param {number} [options.pageSize=10] - Número de documentos por página
 * @param {Object} [options.lastDocument=null] - Último documento de la página anterior
 * @param {string} [options.orderByField='createdAt'] - Campo por el que ordenar
 * @param {string} [options.orderDirection='desc'] - Dirección de ordenación ('asc' o 'desc')
 * @param {Array} [options.filters=[]] - Array de filtros en formato [{field, operator, value}]
 * @returns {Promise<{data: Array, lastVisible: Object, hasMore: boolean}>} - Datos, último documento visible y si hay más
 */
export const getPaginatedDocuments = async (collectionName, options = {}) => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    const {
      pageSize = PAGINATION.DEFAULT_PAGE_SIZE,
      lastDocument = null,
      orderByField = "createdAt",
      orderDirection = "desc",
      filters = [],
    } = options;

    let q = collection(db, collectionName);

    // Aplicar filtros si existen
    if (filters && filters.length > 0) {
      filters.forEach((filter) => {
        q = query(q, where(filter.field, filter.operator, filter.value));
      });
    }

    // Crear consulta base con ordenación
    q = query(q, orderBy(orderByField, orderDirection));

    // Si hay un documento de inicio (paginación), empezar después de él
    if (lastDocument) {
      q = query(q, startAfter(lastDocument));
    }

    // Limitar el número de resultados
    q = query(q, limit(pageSize));

    // Ejecutar consulta
    const snapshot = await getDocs(q);

    // Procesar resultados
    const data = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    const lastVisible =
      snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;

    const hasMore = snapshot.docs.length === pageSize;

    return {
      data,
      lastVisible,
      hasMore,
    };
  } catch (error) {
    console.error(
      `Error getting paginated documents from ${collectionName}:`,
      error,
    );
    throw new Error(`Failed to fetch data: ${error.message}`);
  }
};

/**
 * Obtiene un documento por su ID
 * @param {string} collectionName - Nombre de la colección
 * @param {string} docId - ID del documento
 * @returns {Promise<Object|null>} - Datos del documento o null si no existe
 */
export const getDocumentById = async (collectionName, docId) => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    const docRef = doc(db, collectionName, docId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data(),
      };
    } else {
      return null;
    }
  } catch (error) {
    console.error(
      `Error getting document ${docId} from ${collectionName}:`,
      error,
    );
    throw new Error(`Failed to fetch document: ${error.message}`);
  }
};

/**
 * Función para subir imágenes a Storage con seguimiento de progreso
 * @param {File} file - Archivo a subir
 * @param {string} path - Ruta en Storage (usar STORAGE_PATHS)
 * @param {string} [fileName=null] - Nombre personalizado para el archivo (opcional)
 * @param {Function} [onProgress=null] - Callback para seguimiento de progreso (opcional)
 * @returns {Promise<string>} - URL de descarga del archivo
 */
export const uploadImage = async (
  file,
  path,
  fileName = null,
  onProgress = null,
) => {
  if (!storage || !firebaseStatus.services.storage) {
    throw new Error("Firebase Storage not initialized");
  }

  try {
    // Validar archivo
    if (!file) throw new Error("No file provided");

    // Verificar si es una imagen
    if (!file.type.match("image.*")) {
      throw new Error("Only image files are supported");
    }

    // Generar nombre de archivo único si no se proporciona
    const uniqueFileName = fileName || `${Date.now()}_${file.name}`;
    const storageRef = ref(storage, `${path}/${uniqueFileName}`);

    // Iniciar la carga con seguimiento de progreso
    const uploadTask = uploadBytesResumable(storageRef, file);

    // Devolver una promesa que resuelve cuando se completa la carga
    return new Promise((resolve, reject) => {
      uploadTask.on(
        "state_changed",
        // Progreso
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          if (onProgress && typeof onProgress === "function") {
            onProgress(progress);
          }
        },
        // Error
        (error) => {
          console.error("Upload failed:", error);
          reject(new Error(`Upload failed: ${error.message}`));
        },
        // Completado
        async () => {
          try {
            // Obtener URL de descarga
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(downloadURL);
          } catch (error) {
            reject(new Error(`Failed to get download URL: ${error.message}`));
          }
        },
      );
    });
  } catch (error) {
    console.error("Error uploading image:", error);
    throw error;
  }
};

/**
 * Función para eliminar un archivo de Storage
 * @param {string} fullPath - Ruta completa del archivo
 * @returns {Promise<void>}
 */
export const deleteFile = async (fullPath) => {
  if (!storage || !firebaseStatus.services.storage) {
    throw new Error("Firebase Storage not initialized");
  }

  try {
    const fileRef = ref(storage, fullPath);
    await deleteObject(fileRef);
    return true;
  } catch (error) {
    console.error("Error deleting file:", error);
    throw new Error(`Failed to delete file: ${error.message}`);
  }
};

/**
 * Obtiene la marca de tiempo del servidor para usar en documentos
 * @returns {Object} - Marca de tiempo del servidor para usar en documentos
 */
export const getServerTimestamp = () => {
  return serverTimestamp();
};

/**
 * Verifica el estado de la conexión online
 * @returns {boolean} - true si el usuario está online
 */
export const isOnline = () => {
  return typeof navigator !== "undefined" && navigator.onLine;
};

/**
 * Verifica si la persistencia de Firestore está habilitada
 * @returns {boolean} - true si la persistencia está habilitada
 */
export const isPersistenceEnabled = () => {
  return persistenceEnabled;
};

/**
 * Verifica si un usuario tiene rol de administrador
 * @param {string} userId - ID del usuario a verificar
 * @returns {Promise<boolean>} - true si el usuario es administrador
 */
export const isUserAdmin = async (userId) => {
  if (!db || !firebaseStatus.services.firestore || !userId) {
    return false;
  }

  try {
    const userRef = doc(db, COLLECTIONS.USERS, userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      return false;
    }

    const userData = userDoc.data();
    return userData.role === "admin";
  } catch (error) {
    console.error("Error verificando estado de administrador:", error);
    return false;
  }
};

/**
 * Verifica si un usuario puede acceder a funciones premium o restringidas
 * Los administradores siempre pueden acceder a todas las funciones
 * @param {boolean} isAdmin - Si el usuario es administrador
 * @param {boolean} hasAccess - Si el usuario tiene acceso por otra razón (suscripción, etc.)
 * @returns {boolean} - true si el usuario tiene acceso
 */
export const canAccessFeature = (isAdmin, hasAccess) => {
  // Los administradores siempre tienen acceso completo
  if (isAdmin) return true;

  // Para usuarios normales, verificar el otro criterio
  return hasAccess;
};

/**
 * Establece un usuario como administrador (sólo usar una vez)
 * @param {string} userId - ID del usuario
 * @returns {Promise<boolean>} - true si se estableció correctamente
 */
export const setAdminUser = async (userId) => {
  if (!db || !firebaseStatus.services.firestore || !userId) {
    throw new Error("Firestore no inicializado o userId inválido");
  }

  try {
    const userRef = doc(db, COLLECTIONS.USERS, userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      throw new Error("Usuario no encontrado");
    }

    await userDoc.ref.update({
      role: "admin",
      adminSince: serverTimestamp(),
    });

    console.log(`Usuario ${userId} establecido como administrador`);
    return true;
  } catch (error) {
    console.error("Error estableciendo usuario administrador:", error);
    throw error;
  }
};

/**
 * Esta función debe implementarse para permitir publicar posts
 * @param {string} postId - ID del post pendiente
 * @returns {Promise<string>} - ID del nuevo post publicado
 */
export const publishPendingPost = async (postId) => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    const postRef = doc(db, COLLECTIONS.BLOG_POSTS_PENDING, postId);
    const postSnap = await getDoc(postRef);

    if (!postSnap.exists()) {
      throw new Error("Post not found");
    }

    const postData = postSnap.data();

    // Crear nuevo documento en la colección de posts publicados
    const newPostRef = doc(collection(db, COLLECTIONS.BLOG_POSTS));
    await setDoc(newPostRef, {
      ...postData,
      status: "published",
      publishedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    // Eliminar de pendientes
    await deleteDoc(postRef);

    // Registrar analítica de publicación
    logAnalyticsEvent("post_published", {
      post_id: newPostRef.id,
      category: postData.categories?.[0] || "uncategorized",
    });

    return newPostRef.id;
  } catch (error) {
    console.error("Error publishing post:", error);
    throw new Error(`Failed to publish post: ${error.message}`);
  }
};

/**
 * Registra o elimina un "me gusta" de un usuario en un post
 * @param {string} postId - ID del post
 * @param {string} userId - ID del usuario
 * @returns {Promise<{liked: boolean}>} - Estado final del like
 */
export const togglePostLike = async (postId, userId) => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    const likeRef = doc(db, "post_likes", `${postId}_${userId}`);
    const likeDoc = await getDoc(likeRef);

    if (likeDoc.exists()) {
      // Ya dio like, eliminar
      await deleteDoc(likeRef);

      // Actualizar contador en el post
      const postRef = doc(db, COLLECTIONS.BLOG_POSTS, postId);
      await updateDoc(postRef, {
        likeCount: increment(-1),
      });

      return { liked: false };
    } else {
      // Nuevo like
      await setDoc(likeRef, {
        postId,
        userId,
        timestamp: serverTimestamp(),
      });

      // Actualizar contador en el post
      const postRef = doc(db, COLLECTIONS.BLOG_POSTS, postId);
      await updateDoc(postRef, {
        likeCount: increment(1),
      });

      return { liked: true };
    }
  } catch (error) {
    console.error("Error toggling post like:", error);
    throw new Error(`Error al gestionar like: ${error.message}`);
  }
};

/**
 * Verifica si un usuario ha dado like a un post
 * @param {string} postId - ID del post
 * @param {string} userId - ID del usuario
 * @returns {Promise<boolean>} - true si el usuario ha dado like
 */
export const hasUserLikedPost = async (postId, userId) => {
  if (!db || !firebaseStatus.services.firestore || !userId) {
    return false;
  }

  try {
    const likeRef = doc(db, "post_likes", `${postId}_${userId}`);
    const likeDoc = await getDoc(likeRef);
    return likeDoc.exists();
  } catch (error) {
    console.error("Error checking post like status:", error);
    return false;
  }
};

/**
 * Registra una vista de un post
 * @param {string} postId - ID del post
 * @param {string} [userId] - ID opcional del usuario
 * @returns {Promise<void>}
 */
export const recordPostView = async (postId, userId = null) => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    // Si hay userId, verificar que no haya visto el post antes
    if (userId) {
      const viewRef = doc(db, "post_views", `${postId}_${userId}`);
      const viewDoc = await getDoc(viewRef);

      // Si ya vio el post, no incrementar contador
      if (viewDoc.exists()) {
        return;
      }

      // Registrar que este usuario vio el post
      await setDoc(viewRef, {
        userId,
        postId,
        timestamp: serverTimestamp(),
      });
    }

    // Incrementar contador de vistas
    const postRef = doc(db, COLLECTIONS.BLOG_POSTS, postId);
    await updateDoc(postRef, {
      readCount: increment(1),
    });
  } catch (error) {
    console.error("Error recording post view:", error);
    throw new Error(`Error al registrar vista: ${error.message}`);
  }
};

/**
 * Registra cuando un usuario comparte un post
 * @param {string} postId - ID del post
 * @param {string} platform - Plataforma donde se compartió
 * @param {string} [userId] - ID opcional del usuario
 * @returns {Promise<void>}
 */
export const recordPostShare = async (postId, platform, userId = null) => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    // Incrementar contador general de comparticiones
    const postRef = doc(db, COLLECTIONS.BLOG_POSTS, postId);

    // Actualización atómica con contadores específicos por plataforma
    const updateData = {
      shareCount: increment(1),
    };

    // Si se especifica plataforma, incrementar contador específico
    if (platform) {
      updateData[`platformShares.${platform}`] = increment(1);
    }

    await updateDoc(postRef, updateData);

    // Si hay userId, registrar la acción para análisis
    if (userId) {
      await addDoc(collection(db, "post_shares"), {
        userId,
        postId,
        platform: platform || "unknown",
        timestamp: serverTimestamp(),
      });
    }

    // Registrar analítica
    logAnalyticsEvent("post_shared", {
      platform,
      post_id: postId,
    });
  } catch (error) {
    console.error("Error recording post share:", error);
    throw new Error(`Error al registrar compartición: ${error.message}`);
  }
};

/**
 * Obtiene categorías del foro con paginación y conteo total
 * @param {number} [page=1] - Número de página
 * @param {number} [limitPerPage=PAGINATION.FORUM_PAGE_SIZE] - Resultados por página
 * @returns {Promise<{categories: Array, totalCategories: number, totalPages: number}>}
 */
export const getForumCategories = async (
  page = 1,
  limitPerPage = PAGINATION.FORUM_PAGE_SIZE, // Renombrado para evitar confusión
) => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    const categoriesCollectionRef = collection(
      db,
      COLLECTIONS.FORUM_CATEGORIES,
    );
    const pageSize = parseInt(limitPerPage); // Usar el parámetro renombrado

    // --- PASO 1: Obtener el conteo total de categorías ---
    const countQuery = query(categoriesCollectionRef);
    const totalSnapshot = await getCountFromServer(countQuery);
    const totalCategories = totalSnapshot.data().count;
    const totalPages = Math.ceil(totalCategories / pageSize);

    // --- PASO 2: Construir la consulta para la página actual ---
    let categoriesQuery; // Declarar fuera del if/else

    // --- PASO 3: Aplicar paginación usando startAfter si no es la página 1 ---
    if (page > 1 && totalCategories > 0) {
      // Obtener el último documento de la página anterior como cursor
      const previousPageQuery = query(
        categoriesCollectionRef,
        orderBy("order", "asc"),
        limit((page - 1) * pageSize), // <--- CORREGIDO
      );
      const previousSnapshot = await getDocs(previousPageQuery);

      if (previousSnapshot.docs.length > 0) {
        const lastVisibleDoc =
          previousSnapshot.docs[previousSnapshot.docs.length - 1];
        // Construir la consulta de la página actual
        categoriesQuery = query(
          categoriesCollectionRef,
          orderBy("order", "asc"),
          startAfter(lastVisibleDoc),
          limit(pageSize), // <--- Usar la FUNCIÓN limit con pageSize
        );
      } else {
        console.warn(
          `Paginación inválida solicitada para categorías del foro: página ${page}`,
        );
        return { categories: [], totalCategories, totalPages };
      }
    } else {
      // Consulta para la primera página
      categoriesQuery = query(
        categoriesCollectionRef,
        orderBy("order", "asc"),
        limit(pageSize), // <--- Usar la FUNCIÓN limit con pageSize
      );
    }

    // --- PASO 4: Ejecutar la consulta ---
    const snapshot = await getDocs(categoriesQuery);

    // --- PASO 5: Mapear resultados y añadir info extra ---
    const categoriesWithData = await Promise.all(
      snapshot.docs.map(async (categoryDoc) => {
        const category = { id: categoryDoc.id, ...categoryDoc.data() };

        // Obtener conteo de posts (solo moderados)
        const postsCountQuery = query(
          collection(db, COLLECTIONS.FORUM_POSTS),
          where("categoryId", "==", category.id), // CORREGIDO: Usar 'categoryId' en vez de 'category'
          where("isModerated", "==", true), // Mantener filtro moderado
        );

        try {
          const countSnapshot = await getCountFromServer(postsCountQuery);
          category.postCount = countSnapshot.data().count;
        } catch (countError) {
          console.error(
            `Error contando posts para categoría ${category.id}:`,
            countError,
          );
          category.postCount = 0; // Asignar 0 si falla el conteo
        }

        // Obtener último post activo (moderado)
        const lastPostQuery = query(
          collection(db, COLLECTIONS.FORUM_POSTS),
          where("categoryId", "==", category.id), // CORREGIDO: Usar 'categoryId' en vez de 'category'
          where("isModerated", "==", true), // Mantener filtro moderado
          orderBy("timestamp", "desc"),
          limit(1),
        );

        try {
          const lastPostSnapshot = await getDocs(lastPostQuery);
          category.lastActive = !lastPostSnapshot.empty
            ? lastPostSnapshot.docs[0].data().timestamp
            : category.createdAt || null;
        } catch (lastPostError) {
          console.error(
            `Error obteniendo último post para categoría ${category.id}:`,
            lastPostError,
          );
          category.lastActive = category.createdAt || null;
        }

        return category;
      }),
    );

    // --- PASO 6: Devolver los datos ---
    return {
      categories: categoriesWithData,
      totalCategories,
      totalPages,
    };
  } catch (error) {
    console.error("Error getting forum categories:", error);
    throw new Error(`Failed to fetch forum categories: ${error.message}`);
  }
};

/**
 * Obtiene posts de una categoría con paginación y ordenación
 * @param {string} categoryId - ID de la categoría
 * @param {number} [page=1] - Número de página
 * @param {string} [sortBy='newest'] - Criterio de ordenación
 * @param {number} [limit=10] - Resultados por página
 * @returns {Promise<{posts: Array, totalPosts: number, totalPages: number}>}
 */
export const getForumPosts = async (
  categoryId,
  page = 1,
  sortBy = "newest",
  limit = PAGINATION.FORUM_PAGE_SIZE,
) => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    // Determinar campo y dirección de ordenación
    let orderByField = "timestamp";
    let orderDirection = "desc";

    switch (sortBy) {
      case "oldest":
        orderDirection = "asc";
        break;
      case "mostComments":
        orderByField = "comments";
        orderDirection = "desc";
        break;
      case "newest":
      default:
        break;
    }

    // Obtener posts con filtro de categoría
    const result = await getPaginatedDocuments(COLLECTIONS.FORUM_POSTS, {
      pageSize: limit,
      orderByField,
      orderDirection,
      filters: [
        { field: "categoryId", operator: "==", value: categoryId },
        { field: "isModerated", operator: "==", value: true },
      ],
    });

    // Enriquecer datos con información de autores
    const postsWithAuthors = await Promise.all(
      result.data.map(async (post) => {
        let authorName = "Usuario";
        try {
          const authorDoc = await getDoc(
            doc(db, COLLECTIONS.USERS, post.authorId),
          );
          if (authorDoc.exists()) {
            authorName =
              authorDoc.data().displayName ||
              authorDoc.data().username ||
              "Usuario";
          }
        } catch (error) {
          console.error(`Error getting author for post ${post.id}:`, error);
        }

        return {
          ...post,
          authorName,
        };
      }),
    );

    // Obtener conteo total para calcular páginas
    const baseQuery = query(
      collection(db, COLLECTIONS.FORUM_POSTS),
      where("categoryId", "==", categoryId),
      where("isModerated", "==", true),
    );
    const totalSnapshot = await getCountFromServer(baseQuery);
    const totalPosts = totalSnapshot.data().count;

    return {
      posts: postsWithAuthors,
      totalPosts,
      totalPages: Math.ceil(totalPosts / limit),
      hasMore: result.hasMore,
      lastVisible: result.lastVisible,
    };
  } catch (error) {
    console.error(
      `Error getting forum posts for category ${categoryId}:`,
      error,
    );
    throw new Error(`Failed to fetch forum posts: ${error.message}`);
  }
};

/**
 * Obtiene detalles de un post específico
 * @param {string} postId - ID del post
 * @returns {Promise<Object>} - Datos completos del post
 */
export const getForumPostDetails = async (postId) => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    // Obtener el post
    const postData = await getDocumentById(COLLECTIONS.FORUM_POSTS, postId);

    if (!postData) {
      throw new Error("Post not found");
    }

    // Obtener info del autor
    let author = { name: "Usuario" };
    try {
      const authorDoc = await getDoc(
        doc(db, COLLECTIONS.USERS, postData.authorId),
      );
      if (authorDoc.exists()) {
        author = {
          name:
            authorDoc.data().displayName ||
            authorDoc.data().username ||
            "Usuario",
          photoURL: authorDoc.data().photoURL || null,
          uid: postData.authorId,
        };
      }
    } catch (error) {
      console.error(`Error getting author for post ${postId}:`, error);
    }

    // Obtener info de la categoría
    let categoryName = "General";
    try {
      const categoryDoc = await getDoc(
        doc(db, COLLECTIONS.FORUM_CATEGORIES, postData.categoryId),
      );
      if (categoryDoc.exists()) {
        categoryName = categoryDoc.data().name;
      }
    } catch (error) {
      console.error(`Error getting category for post ${postId}:`, error);
    }

    return {
      ...postData,
      author,
      categoryName,
    };
  } catch (error) {
    console.error(`Error getting forum post ${postId}:`, error);
    throw new Error(`Failed to fetch forum post: ${error.message}`);
  }
};

/**
 * Crea un nuevo post en el foro
 * @param {Object} postData - Datos del post
 * @param {string} postData.title - Título del post
 * @param {string} postData.content - Contenido del post
 * @param {string} postData.categoryId - ID de la categoría
 * @param {string} postData.authorId - ID del autor
 * @param {string} [postData.imageUrl] - URL de imagen opcional
 * @param {boolean} [postData.isPremium=false] - Si es contenido premium
 * @returns {Promise<{postId: string}>} - ID del post creado
 */
export const createForumPost = async (postData) => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    const {
      title,
      content,
      categoryId,
      authorId,
      imageUrl = null,
      isPremium = false,
    } = postData;

    // Validaciones básicas
    if (!title || title.trim().length < 3) {
      throw new Error("El título debe tener al menos 3 caracteres");
    }

    if (!content || content.trim().length < 10) {
      throw new Error("El contenido debe tener al menos 10 caracteres");
    }

    if (!categoryId) {
      throw new Error("Debes seleccionar una categoría");
    }

    // Obtener información del usuario
    let userData = { role: "user" }; // Valor por defecto
    let authorName = "Usuario";
    try {
      const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, authorId));
      if (userDoc.exists()) {
        userData = userDoc.data();
        authorName = userData.displayName || userData.username || "Usuario";
      }
    } catch (error) {
      console.error(`Error getting user info for ${authorId}:`, error);
    }

    // Crear el post
    const postRef = doc(collection(db, COLLECTIONS.FORUM_POSTS));
    const timestamp = serverTimestamp();

    await setDoc(postRef, {
      title: title.trim(),
      content: content.trim(),
      categoryId,
      authorId,
      authorName,
      timestamp,
      lastUpdated: timestamp,
      comments: 0,
      likes: 0,
      views: 0,
      isPaid: isPremium, // Convertir isPremium a isPaid para consistencia
      imageUrl,
      isModerated: userData.role === "admin" || userData.role === "moderator", // Cambiado para moderadores
      reports: 0,
    });

    // Actualizar contador en categoría
    const categoryRef = doc(db, COLLECTIONS.FORUM_CATEGORIES, categoryId);
    await updateDoc(categoryRef, {
      postCount: increment(1),
      lastUpdated: timestamp,
    });

    // Registrar analítica
    logAnalyticsEvent("forum_post_created", {
      category_id: categoryId,
      is_premium: isPremium,
    });

    return { postId: postRef.id };
  } catch (error) {
    console.error("Error creating forum post:", error);
    throw new Error(`Failed to create forum post: ${error.message}`);
  }
};

/**
 * Registra la vista de un post del foro
 * @param {string} postId - ID del post
 * @param {string} [userId=null] - ID opcional del usuario
 * @returns {Promise<{counted: boolean}>}
 */
export const recordForumPostView = async (postId, userId = null) => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    // Si hay userId, verificar que no haya visto el post antes
    if (userId) {
      const viewRef = doc(db, "forum_post_views", `${postId}_${userId}`);
      const viewDoc = await getDoc(viewRef);

      // Si ya vio el post, no incrementar contador
      if (viewDoc.exists()) {
        return { counted: false };
      }

      // Registrar que este usuario vio el post
      await setDoc(viewRef, {
        userId,
        postId,
        timestamp: serverTimestamp(),
      });
    }

    // Incrementar contador de vistas
    const postRef = doc(db, COLLECTIONS.FORUM_POSTS, postId);
    await updateDoc(postRef, {
      views: increment(1),
    });

    return { counted: true };
  } catch (error) {
    console.error(`Error recording view for forum post ${postId}:`, error);
    throw new Error(`Error recording view: ${error.message}`);
  }
};

/**
 * Da/quita like a un post del foro
 * @param {string} postId - ID del post
 * @param {string} userId - ID del usuario
 * @returns {Promise<{liked: boolean}>} - Estado final del like
 */
export const toggleForumPostLike = async (postId, userId) => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    const likeRef = doc(db, "forum_post_likes", `${postId}_${userId}`);
    const likeDoc = await getDoc(likeRef);

    if (likeDoc.exists()) {
      // Ya dio like, eliminar
      await deleteDoc(likeRef);

      // Actualizar contador en el post
      const postRef = doc(db, COLLECTIONS.FORUM_POSTS, postId);
      await updateDoc(postRef, {
        likes: increment(-1),
      });

      return { liked: false };
    } else {
      // Nuevo like
      await setDoc(likeRef, {
        userId,
        postId,
        timestamp: serverTimestamp(),
      });

      // Actualizar contador en el post
      const postRef = doc(db, COLLECTIONS.FORUM_POSTS, postId);
      await updateDoc(postRef, {
        likes: increment(1),
      });

      return { liked: true };
    }
  } catch (error) {
    console.error(`Error toggling like for forum post ${postId}:`, error);
    throw new Error(`Error toggling like: ${error.message}`);
  }
};

/**
 * Verifica si un usuario ha dado like a un post del foro
 * @param {string} postId - ID del post
 * @param {string} userId - ID del usuario
 * @returns {Promise<boolean>} - true si el usuario ha dado like
 */
export const hasUserLikedForumPost = async (postId, userId) => {
  if (!db || !firebaseStatus.services.firestore || !userId) {
    return false;
  }

  try {
    const likeRef = doc(db, "forum_post_likes", `${postId}_${userId}`);
    const likeDoc = await getDoc(likeRef);
    return likeDoc.exists();
  } catch (error) {
    console.error(
      `Error checking like status for forum post ${postId}:`,
      error,
    );
    return false;
  }
};

/**
 * Obtiene comentarios de un post con paginación
 * @param {string} postId - ID del post
 * @param {number} [page=1] - Número de página
 * @param {number} [limit=20] - Resultados por página
 * @returns {Promise<{comments: Array, totalComments: number, totalPages: number}>}
 */
export const getForumComments = async (
  postId,
  page = 1,
  limit = PAGINATION.COMMENTS_PAGE_SIZE,
) => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    const result = await getPaginatedDocuments(COLLECTIONS.FORUM_COMMENTS, {
      pageSize: limit,
      orderByField: "timestamp",
      orderDirection: "desc",
      filters: [
        { field: "postId", operator: "==", value: postId },
        { field: "approved", operator: "==", value: true },
      ],
    });

    // Enriquecer con datos de autor si no están incluidos
    const commentsWithAuthors = await Promise.all(
      result.data.map(async (comment) => {
        let authorName = comment.authorName || "Usuario";
        let authorPhoto = comment.authorPhoto || null;

        if (!comment.authorName && comment.userId) {
          try {
            const userDoc = await getDoc(
              doc(db, COLLECTIONS.USERS, comment.userId),
            );
            if (userDoc.exists()) {
              authorName =
                userDoc.data().displayName ||
                userDoc.data().username ||
                "Usuario";
              authorPhoto = userDoc.data().photoURL || null;
            }
          } catch (error) {
            console.error(
              `Error getting author for comment ${comment.id}:`,
              error,
            );
          }
        }

        return {
          ...comment,
          authorName,
          authorPhoto,
        };
      }),
    );

    // Obtener conteo total para calcular páginas
    const baseQuery = query(
      collection(db, COLLECTIONS.FORUM_COMMENTS),
      where("postId", "==", postId),
      where("approved", "==", true),
    );
    const totalSnapshot = await getCountFromServer(baseQuery);
    const totalComments = totalSnapshot.data().count;

    return {
      comments: commentsWithAuthors,
      totalComments,
      totalPages: Math.ceil(totalComments / limit),
      hasMore: result.hasMore,
      lastVisible: result.lastVisible,
    };
  } catch (error) {
    console.error(`Error getting comments for forum post ${postId}:`, error);
    throw new Error(`Failed to fetch comments: ${error.message}`);
  }
};

/**
 * Añade un comentario a un post del foro
 * @param {Object} commentData - Datos del comentario
 * @param {string} commentData.postId - ID del post
 * @param {string} commentData.userId - ID del autor
 * @param {string} commentData.content - Contenido del comentario
 * @returns {Promise<{commentId: string}>} - ID del comentario creado
 */
export const addForumComment = async (commentData) => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    const { postId, userId, content } = commentData;

    // Validación básica
    if (!content || content.trim().length < 2) {
      throw new Error("El comentario debe tener al menos 2 caracteres");
    }

    // Obtener información del usuario
    let userData = { role: "user" }; // Valor por defecto
    let authorName = "Usuario";
    try {
      const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, userId));
      if (userDoc.exists()) {
        userData = userDoc.data();
        authorName = userData.displayName || userData.username || "Usuario";
      }
    } catch (error) {
      console.error(`Error getting user info for ${userId}:`, error);
    }

    // Crear el comentario
    const commentRef = doc(collection(db, COLLECTIONS.FORUM_COMMENTS));
    await setDoc(commentRef, {
      postId,
      userId,
      content: content.trim(),
      authorName,
      authorPhoto: userData.photoURL,
      timestamp: serverTimestamp(),
      approved: userData.role === "admin" || userData.role === "moderator",
    });

    // Actualizar contador en el post
    const postRef = doc(db, COLLECTIONS.FORUM_POSTS, postId);
    await updateDoc(postRef, {
      comments: increment(1),
      lastUpdated: serverTimestamp(),
    });

    return { commentId: commentRef.id };
  } catch (error) {
    console.error("Error adding forum comment:", error);
    throw new Error(`Failed to add comment: ${error.message}`);
  }
};

/**
 * Elimina un comentario del foro
 * @param {string} commentId - ID del comentario
 * @param {string} userId - ID del usuario que intenta eliminar
 * @returns {Promise<boolean>} - true si se eliminó correctamente
 */
export const deleteForumComment = async (commentId, userId) => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    // Obtener el comentario
    const commentRef = doc(db, COLLECTIONS.FORUM_COMMENTS, commentId);
    const commentDoc = await getDoc(commentRef);

    if (!commentDoc.exists()) {
      throw new Error("Comentario no encontrado");
    }

    const commentData = commentDoc.data();

    // Verificar permisos
    const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, userId));
    const isAdmin = userDoc.exists() && userDoc.data().role === "admin";

    if (commentData.userId !== userId && !isAdmin) {
      throw new Error("No tienes permiso para eliminar este comentario");
    }

    // Eliminar el comentario
    await deleteDoc(commentRef);

    // Decrementar contador en el post
    const postRef = doc(db, COLLECTIONS.FORUM_POSTS, commentData.postId);
    await updateDoc(postRef, {
      comments: increment(-1),
      lastUpdated: serverTimestamp(),
    });

    return true;
  } catch (error) {
    console.error(`Error deleting forum comment ${commentId}:`, error);
    throw new Error(`Failed to delete comment: ${error.message}`);
  }
};

/**
 * Obtiene el perfil de un usuario en el foro
 * @param {string} userId - ID del usuario
 * @param {number} tabValue - Pestaña seleccionada (0=posts, 1=comentarios)
 * @param {number} [page=1] - Número de página
 * @param {number} [limit=10] - Resultados por página
 * @returns {Promise<{stats: Object, items: Array, totalItems: number, totalPages: number}>}
 */
export const getUserForumProfile = async (
  userId,
  tabValue = 0,
  page = 1,
  limit = 10,
) => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    // Recuperar estadísticas del usuario
    const stats = {
      totalPosts: 0,
      totalComments: 0,
      totalLikes: 0,
    };

    // Contar posts del usuario
    const postsCountQuery = query(
      collection(db, COLLECTIONS.FORUM_POSTS),
      where("authorId", "==", userId),
    );
    const postsCountSnapshot = await getCountFromServer(postsCountQuery);
    stats.totalPosts = postsCountSnapshot.data().count;

    // Contar comentarios del usuario
    const commentsCountQuery = query(
      collection(db, COLLECTIONS.FORUM_COMMENTS),
      where("userId", "==", userId),
    );
    const commentsCountSnapshot = await getCountFromServer(commentsCountQuery);
    stats.totalComments = commentsCountSnapshot.data().count;

    // Contar likes recibidos
    if (stats.totalPosts > 0) {
      const postsQuery = query(
        collection(db, COLLECTIONS.FORUM_POSTS),
        where("authorId", "==", userId),
      );
      const postsSnapshot = await getDocs(postsQuery);

      postsSnapshot.forEach((doc) => {
        stats.totalLikes += doc.data().likes || 0;
      });
    }

    // Obtener items según la pestaña seleccionada
    let items = [];
    let totalItems = 0;

    if (tabValue === 0) {
      // Pestaña de posts
      totalItems = stats.totalPosts;

      if (totalItems > 0) {
        const result = await getPaginatedDocuments(COLLECTIONS.FORUM_POSTS, {
          pageSize: limit,
          orderByField: "timestamp",
          orderDirection: "desc",
          filters: [{ field: "authorId", operator: "==", value: userId }],
        });

        items = result.data.map((post) => ({
          id: post.id,
          title: post.title,
          timestamp: post.timestamp,
          comments: post.comments || 0,
          likes: post.likes || 0,
          isPremium: post.isPremium || false,
          isModerated: post.isModerated !== false,
        }));
      }
    } else {
      // Pestaña de comentarios
      totalItems = stats.totalComments;

      if (totalItems > 0) {
        const result = await getPaginatedDocuments(COLLECTIONS.FORUM_COMMENTS, {
          pageSize: limit,
          orderByField: "timestamp",
          orderDirection: "desc",
          filters: [{ field: "userId", operator: "==", value: userId }],
        });

        // Obtener títulos de los posts para cada comentario
        items = await Promise.all(
          result.data.map(async (comment) => {
            const commentData = {
              id: comment.id,
              content: comment.content,
              timestamp: comment.timestamp,
              postId: comment.postId,
            };

            try {
              const postDoc = await getDoc(
                doc(db, COLLECTIONS.FORUM_POSTS, comment.postId),
              );
              if (postDoc.exists()) {
                commentData.postTitle =
                  postDoc.data().title || "Post sin título";
              }
            } catch (error) {
              console.error(
                `Error getting post for comment ${comment.id}:`,
                error,
              );
            }

            return commentData;
          }),
        );
      }
    }

    return {
      stats,
      items,
      totalItems,
      totalPages: Math.ceil(totalItems / limit),
    };
  } catch (error) {
    console.error(`Error getting forum profile for user ${userId}:`, error);
    throw new Error(`Failed to get forum profile: ${error.message}`);
  }
};

/**
 * Obtiene las conversaciones del chatbot para un usuario
 * @param {string} userId - ID del usuario
 * @param {number} [limit=10] - Número máximo de conversaciones a obtener
 * @returns {Promise<Array>} - Lista de conversaciones
 */
export const getChatConversations = async (userId, limit = 10) => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    const conversationsRef = query(
      collection(db, COLLECTIONS.CHAT_CONVERSATIONS),
      where("userId", "==", userId),
      orderBy("updatedAt", "desc"),
      limit(limit),
    );

    const snapshot = await getDocs(conversationsRef);

    const conversations = [];
    snapshot.forEach((doc) => {
      conversations.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    return conversations;
  } catch (error) {
    console.error(`Error getting chat conversations for ${userId}:`, error);
    throw new Error(`Failed to get conversations: ${error.message}`);
  }
};

/**
 * Crea una nueva conversación de chatbot
 * @param {string} userId - ID del usuario
 * @param {string} title - Título de la conversación
 * @returns {Promise<{id: string}>} - ID de la nueva conversación
 */
export const createChatConversation = async (
  userId,
  title = "Nueva conversación",
) => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    const timestamp = serverTimestamp();

    const conversationRef = doc(collection(db, COLLECTIONS.CHAT_CONVERSATIONS));
    await setDoc(conversationRef, {
      title,
      userId,
      messageCount: 0,
      createdAt: timestamp,
      updatedAt: timestamp,
    });

    return { id: conversationRef.id };
  } catch (error) {
    console.error(`Error creating chat conversation for ${userId}:`, error);
    throw new Error(`Failed to create conversation: ${error.message}`);
  }
};

/**
 * Obtiene los mensajes de una conversación
 * @param {string} conversationId - ID de la conversación
 * @param {string} userId - ID del usuario para verificar permisos
 * @returns {Promise<Array>} - Lista de mensajes
 */
export const getChatMessages = async (conversationId, userId) => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    // Verificar permisos
    const conversationRef = doc(
      db,
      COLLECTIONS.CHAT_CONVERSATIONS,
      conversationId,
    );
    const conversationDoc = await getDoc(conversationRef);

    if (!conversationDoc.exists()) {
      throw new Error("Conversación no encontrada");
    }

    if (conversationDoc.data().userId !== userId) {
      throw new Error("No autorizado");
    }

    // Obtener mensajes
    const messagesRef = query(
      collection(db, COLLECTIONS.CHAT_MESSAGES),
      where("conversationId", "==", conversationId),
      orderBy("timestamp", "asc"),
    );

    const snapshot = await getDocs(messagesRef);

    const messages = [];
    snapshot.forEach((doc) => {
      messages.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    return messages;
  } catch (error) {
    console.error(`Error getting chat messages for ${conversationId}:`, error);
    throw new Error(`Failed to get messages: ${error.message}`);
  }
};

/**
 * Guarda un mensaje en una conversación de chatbot
 * @param {Object} messageData - Datos del mensaje
 * @param {string} messageData.conversationId - ID de la conversación
 * @param {string} messageData.userId - ID del usuario
 * @param {string} messageData.text - Texto del mensaje
 * @param {string} messageData.sender - Remitente ('user' o 'bot')
 * @returns {Promise<{id: string}>} - ID del mensaje guardado
 */
export const saveChatMessage = async (messageData) => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    const { conversationId, userId, text, sender } = messageData;

    // Verificar permisos
    const conversationRef = doc(
      db,
      COLLECTIONS.CHAT_CONVERSATIONS,
      conversationId,
    );
    const conversationDoc = await getDoc(conversationRef);

    if (!conversationDoc.exists()) {
      throw new Error("Conversación no encontrada");
    }

    if (conversationDoc.data().userId !== userId) {
      throw new Error("No autorizado");
    }

    // Guardar mensaje
    const timestamp = serverTimestamp();

    const messageRef = doc(collection(db, COLLECTIONS.CHAT_MESSAGES));
    await setDoc(messageRef, {
      conversationId,
      userId,
      text,
      sender,
      timestamp,
    });

    // Actualizar conversación
    await updateDoc(conversationRef, {
      messageCount: increment(1),
      updatedAt: timestamp,
      lastMessage: text.substring(0, 100),
    });

    return { id: messageRef.id };
  } catch (error) {
    console.error(`Error saving chat message:`, error);
    throw new Error(`Failed to save message: ${error.message}`);
  }
};

/**
 * Guarda una respuesta favorita
 * @param {Object} responseData - Datos de la respuesta
 * @param {string} responseData.userId - ID del usuario
 * @param {string} responseData.text - Texto de la respuesta
 * @param {string} responseData.title - Título opcional
 * @param {string} responseData.category - Categoría opcional
 * @returns {Promise<{id: string}>} - ID de la respuesta guardada
 */
export const saveFavoriteResponse = async (responseData) => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    const { userId, text, title, category = "general" } = responseData;

    // Verificar límite de respuestas guardadas
    const savedCountQuery = query(
      collection(db, COLLECTIONS.SAVED_RESPONSES),
      where("userId", "==", userId),
    );

    const countSnapshot = await getCountFromServer(savedCountQuery);
    const currentCount = countSnapshot.data().count;

    // Verificar límites según suscripción
    const userRef = doc(db, COLLECTIONS.USERS, userId);
    const userDoc = await getDoc(userRef);
    const isSubscribed =
      userDoc.exists() && userDoc.data().subscriptionStatus === "active";
    const maxResponses = isSubscribed ? 50 : 10;

    if (currentCount >= maxResponses) {
      throw new Error(
        `Has alcanzado el límite de ${maxResponses} respuestas guardadas`,
      );
    }

    // Guardar respuesta
    const responseRef = doc(collection(db, COLLECTIONS.SAVED_RESPONSES));
    const timestamp = serverTimestamp();

    await setDoc(responseRef, {
      userId,
      text,
      title: title || text.substring(0, 30) + "...",
      category,
      createdAt: timestamp,
    });

    return { id: responseRef.id };
  } catch (error) {
    console.error(`Error saving favorite response:`, error);
    throw new Error(`Failed to save response: ${error.message}`);
  }
};

/**
 * Obtiene respuestas guardadas de un usuario
 * @param {string} userId - ID del usuario
 * @param {string} [category='all'] - Categoría para filtrar
 * @returns {Promise<Array>} - Lista de respuestas guardadas
 */
export const getFavoriteResponses = async (userId, category = "all") => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    let queryRef = query(
      collection(db, COLLECTIONS.SAVED_RESPONSES),
      where("userId", "==", userId),
      orderBy("createdAt", "desc"),
    );

    // Filtrar por categoría si no es 'all'
    if (category !== "all") {
      queryRef = query(queryRef, where("category", "==", category));
    }

    const snapshot = await getDocs(queryRef);

    const responses = [];
    snapshot.forEach((doc) => {
      responses.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    return responses;
  } catch (error) {
    console.error(`Error getting favorite responses for ${userId}:`, error);
    throw new Error(`Failed to get responses: ${error.message}`);
  }
};

/**
 * Obtiene información especializada sobre síndrome de Down
 * @param {string} language - Código de idioma
 * @param {string} category - Categoría de información
 * @returns {Promise<Object>} - Datos de la categoría solicitada
 */
export const getDownSyndromeInfo = async (language = "es", category) => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    const infoRef = doc(
      db,
      COLLECTIONS.DOWN_SYNDROME_INFO,
      language,
      "categories",
      category,
    );
    const infoDoc = await getDoc(infoRef);

    if (!infoDoc.exists()) {
      throw new Error("Información no encontrada");
    }

    return infoDoc.data();
  } catch (error) {
    console.error(`Error getting info for ${category} in ${language}:`, error);
    throw new Error(`Failed to get information: ${error.message}`);
  }
};

/**
 * Obtiene el uso del chatbot para un usuario
 * @param {string} userId - ID del usuario
 * @returns {Promise<Object>} - Estadísticas de uso y límites
 */
export const getChatbotUsage = async (userId) => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    // Obtener información del usuario
    const userRef = doc(db, COLLECTIONS.USERS, userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      throw new Error("Usuario no encontrado");
    }

    const userData = userDoc.data();
    const isSubscribed = userData.subscriptionStatus === "active";
    const isAdmin = userData.role === "admin";

    // Calcular fechas para consultas
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    // Consultar mensajes de hoy
    const messagesRef = collection(db, COLLECTIONS.CHAT_MESSAGES);
    const todayQuery = query(
      messagesRef,
      where("userId", "==", userId),
      where("sender", "==", "user"),
      where("timestamp", ">=", today),
    );

    const monthQuery = query(
      messagesRef,
      where("userId", "==", userId),
      where("sender", "==", "user"),
      where("timestamp", ">=", startOfMonth),
    );

    const [todayCount, monthCount] = await Promise.all([
      getCountFromServer(todayQuery),
      getCountFromServer(monthQuery),
    ]);

    return {
      usage: {
        daily: todayCount.data().count,
        monthly: monthCount.data().count,
      },
      limits: {
        daily: isSubscribed ? 50 : 10,
        monthly: isSubscribed ? 1000 : 100,
        audioEnabled: isSubscribed,
        hasUnlimitedAccess: isAdmin,
      },
      isSubscribed,
    };
  } catch (error) {
    console.error("Error obteniendo uso del chatbot:", error);
    throw new Error("Error al verificar el uso del chatbot");
  }
};

/**
 * Obtiene especialistas con filtrado y paginación
 * @param {Object} options - Opciones de filtrado y paginación
 * @param {string} [options.language='es'] - Idioma de los especialistas
 * @param {string} [options.country=null] - País para filtrar
 * @param {string} [options.city=null] - Ciudad para filtrar
 * @param {string} [options.specialty=null] - Especialidad para filtrar
 * @param {string} [options.search=null] - Término de búsqueda
 * @param {number} [options.page=1] - Página a obtener
 * @param {number} [options.limit=9] - Resultados por página
 * @param {Object} [options.lastDocument=null] - Último documento para paginación
 * @returns {Promise<{specialists: Array, totalSpecialists: number, totalPages: number, lastVisible: Object}>}
 */
export const getSpecialists = async (options = {}) => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    const {
      language = "es",
      country = null,
      city = null,
      specialty = null,
      search = null,
      page = 1,
      limit = 9,
      lastDocument = null,
    } = options;

    // Referencia base a la colección de especialistas para el idioma actual
    const specialistsRef = collection(
      db,
      COLLECTIONS.SPECIALISTS_FLAT,
      language,
      "entries",
    );

    // Construir la consulta base
    let baseQuery = specialistsRef;

    // Aplicar filtros
    if (country && country !== "all") {
      baseQuery = query(baseQuery, where("country", "==", country));
    }

    if (city && city !== "all") {
      baseQuery = query(baseQuery, where("city", "==", city));
    }

    if (specialty && specialty !== "all") {
      baseQuery = query(baseQuery, where("specialty", "==", specialty));
    }

    // Obtener el conteo total para la paginación
    const totalSnapshot = await getCountFromServer(baseQuery);
    const totalSpecialists = totalSnapshot.data().count;

    // Configurar paginación
    let specialistsQuery;

    if (lastDocument) {
      specialistsQuery = query(
        baseQuery,
        orderBy("name"),
        startAfter(lastDocument),
        limit(limit),
      );
    } else {
      // Usar la variable page para simular offset en casos donde no tenemos lastDocument
      if (page > 1) {
        // Enfoque híbrido: obtener documentos hasta la página anterior y usar el último como cursor
        const pageSize = parseInt(limit);
        const previousPageQuery = query(
          baseQuery,
          orderBy("name"),
          limit((page - 1) * pageSize),
        );

        const previousSnapshot = await getDocs(previousPageQuery);

        if (previousSnapshot.docs.length > 0) {
          const lastVisibleDoc =
            previousSnapshot.docs[previousSnapshot.docs.length - 1];
          // Construir la consulta de la página actual
          specialistsQuery = query(
            baseQuery,
            orderBy("name"),
            startAfter(lastVisibleDoc),
            limit(pageSize),
          );
        } else {
          // Si no hay suficientes documentos para llegar a la página solicitada
          specialistsQuery = query(baseQuery, orderBy("name"), limit(pageSize));
        }
      } else {
        // Para la primera página (page=1), usar consulta simple
        specialistsQuery = query(
          baseQuery,
          orderBy("name"),
          limit(parseInt(limit)),
        );
      }
    }

    // Ejecutar la consulta
    const snapshot = await getDocs(specialistsQuery);

    const specialists = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    // Si hay un término de búsqueda, filtrar los resultados en memoria
    // Nota: esto es ineficiente para colecciones grandes, idealmente
    // se usaría Algolia o Firebase Functions para implementar búsqueda de texto
    if (search && search.trim() !== "") {
      const searchLower = search.toLowerCase();
      return {
        specialists: specialists.filter(
          (specialist) =>
            specialist.name?.toLowerCase().includes(searchLower) ||
            specialist.specialty?.toLowerCase().includes(searchLower) ||
            specialist.description?.toLowerCase().includes(searchLower),
        ),
        totalSpecialists,
        totalPages: Math.ceil(totalSpecialists / limit),
        lastVisible: snapshot.docs.length
          ? snapshot.docs[snapshot.docs.length - 1]
          : null,
      };
    }

    return {
      specialists,
      totalSpecialists,
      totalPages: Math.ceil(totalSpecialists / limit),
      lastVisible: snapshot.docs.length
        ? snapshot.docs[snapshot.docs.length - 1]
        : null,
    };
  } catch (error) {
    console.error("Error obteniendo especialistas:", error);
    throw new Error(`Failed to fetch specialists: ${error.message}`);
  }
};

/**
 * Obtiene metadatos para filtros de especialistas (países, ciudades, especialidades)
 * @param {string} [language='es'] - Idioma de los metadatos
 * @returns {Promise<{countries: Array, cities: Array, specialties: Array}>}
 */
export const getSpecialistsMetadata = async (language = "es") => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    const metadataRef = doc(db, COLLECTIONS.SPECIALISTS_METADATA, language);
    const metadataDoc = await getDoc(metadataRef);

    if (!metadataDoc.exists()) {
      console.log(`No metadata found for language ${language}`);
      return {
        countries: [],
        cities: [],
        specialties: [],
      };
    }

    const data = metadataDoc.data();

    // Filtrar valores como 'Desconocido' o 'Desconocida'
    const countries = Array.isArray(data.countries)
      ? data.countries.filter((c) => c !== "Desconocido").sort()
      : [];

    const cities = Array.isArray(data.cities)
      ? data.cities.filter((c) => c !== "Desconocida").sort()
      : [];

    const specialties = Array.isArray(data.specialties)
      ? data.specialties.sort()
      : [];

    return {
      countries,
      cities,
      specialties,
    };
  } catch (error) {
    console.error(
      `Error obteniendo metadata de especialistas para ${language}:`,
      error,
    );
    throw new Error(`Failed to fetch specialists metadata: ${error.message}`);
  }
};

/**
 * Obtiene los detalles de un especialista específico
 * @param {string} compositeId - ID compuesto del especialista (ej: "es_XYZ123")
 * @param {string} [currentUILanguage='es'] - Idioma actual de la UI (puede ser útil para traducciones futuras, pero no para la ruta del doc)
 * @returns {Promise<Object>} - Datos del especialista
 */
export const getSpecialistById = async (
  compositeId,
  currentUILanguage = "es",
) => {
  // Parámetro 'language' renombrado a 'currentUILanguage' para claridad
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    // --- INICIO DE LA MODIFICACIÓN ---
    // 1. Dividir el compositeId en el idioma original del documento y el ID real del documento
    const parts = compositeId.split("_");
    if (parts.length < 2) {
      // Si el ID no tiene el formato esperado, podría ser un ID antiguo o incorrecto.
      // Podrías manejar esto de forma diferente, pero por ahora lanzaremos un error.
      console.error(`Invalid composite ID format: ${compositeId}`);
      throw new Error("Invalid specialist ID format.");
    }

    const originalDocLanguage = parts[0]; // ej: "es"
    const actualDocId = parts.slice(1).join("_"); // ej: "XYZ123" (maneja IDs que puedan tener guiones bajos)

    // 2. Construir la referencia correcta al documento
    const specialistRef = doc(
      db,
      COLLECTIONS.SPECIALISTS_FLAT, // Colección base
      originalDocLanguage, // Subcolección del idioma original del documento
      "entries", // Subcolección 'entries'
      actualDocId, // ID real del documento
    );
    // --- FIN DE LA MODIFICACIÓN ---

    const specialistDoc = await getDoc(specialistRef);

    if (!specialistDoc.exists()) {
      // Este error ahora solo debería ocurrir si el 'actualDocId' realmente no existe en esa ruta
      throw new Error("Specialist not found");
    }

    // Podrías querer devolver el currentUILanguage si lo necesitas más adelante para algo
    // o incluso el originalDocLanguage si es relevante para el componente que llama.
    return {
      id: compositeId, // Devolver el ID compuesto original que se usó para la URL y las keys
      ...specialistDoc.data(),
      // originalLanguage: originalDocLanguage, // Opcional: si necesitas saber el idioma original del dato
    };
  } catch (error) {
    console.error(
      `Error obteniendo especialista con ID compuesto ${compositeId}:`,
      error,
    );
    // Mantener el mensaje de error genérico para el usuario, pero el log de consola es más específico.
    throw new Error(`Failed to fetch specialist details: ${error.message}`);
  }
};

/**
 * Obtiene noticias recientes sobre síndrome de Down
 * @param {Object} options - Opciones de consulta
 * @param {string} [options.language='es'] - Idioma de las noticias
 * @param {number} [options.limit=4] - Número de noticias a obtener
 * @returns {Promise<Array>} - Lista de noticias
 */
export const getDownSyndromeNews = async (options = {}) => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    const { language = "es", limit = 4 } = options;

    const newsRef = collection(db, COLLECTIONS.DOWN_SYNDROME_NEWS);
    const newsQuery = query(
      newsRef,
      where("language", "==", language),
      orderBy("timestamp", "desc"),
      limit(parseInt(limit)),
    );

    const snapshot = await getDocs(newsQuery);

    return snapshot.docs.map((doc) => ({
      id: doc.id,
      title: doc.data().title || "Sin título",
      summary: doc.data().summary || "",
      source: doc.data().source || "Fuente desconocida",
      date: doc.data().date || "",
      link: doc.data().link || "#",
      language: doc.data().language || "es",
      timestamp: doc.data().timestamp?.toDate() || new Date(),
    }));
  } catch (error) {
    console.error("Error obteniendo noticias sobre síndrome de Down:", error);
    throw new Error(`Failed to fetch Down syndrome news: ${error.message}`);
  }
};

/**
 * Envía un contacto/valoración a un especialista
 * @param {Object} contactData - Datos del contacto
 * @param {string} contactData.specialistId - ID del especialista
 * @param {string} contactData.userId - ID del usuario
 * @param {string} contactData.message - Mensaje/consulta
 * @param {number} [contactData.rating=0] - Valoración opcional (0-5)
 * @param {string} [contactData.language='es'] - Idioma del contacto
 * @returns {Promise<{contactId: string}>} - ID del contacto creado
 */
export const contactSpecialist = async (contactData) => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    const {
      specialistId,
      userId,
      message,
      rating = 0,
      language = "es",
    } = contactData;

    // Validación básica
    if (!message || message.trim().length < 10) {
      throw new Error("El mensaje debe tener al menos 10 caracteres");
    }

    // Verificar que el especialista existe
    const specialistRef = doc(
      db,
      COLLECTIONS.SPECIALISTS_FLAT,
      language,
      "entries",
      specialistId,
    );
    const specialistDoc = await getDoc(specialistRef);

    if (!specialistDoc.exists()) {
      throw new Error("Especialista no encontrado");
    }

    // Crear el contacto
    const contactsCollection = collection(db, "specialist_contacts");
    const contactRef = doc(contactsCollection);

    await setDoc(contactRef, {
      specialistId,
      userId,
      message: message.trim(),
      rating: rating || 0,
      timestamp: serverTimestamp(),
      status: "pending",
      language,
    });

    // Si hay valoración, actualizar la valoración del especialista
    if (rating && rating > 0) {
      // Obtener la valoración actual del especialista
      const specialistRating = specialistDoc.data().rating || 0;
      const ratingCount = specialistDoc.data().ratingCount || 0;

      // Calcular nueva valoración
      const newRatingCount = ratingCount + 1;
      const newRating =
        (specialistRating * ratingCount + rating) / newRatingCount;

      // Actualizar especialista
      await updateDoc(specialistRef, {
        rating: newRating,
        ratingCount: newRatingCount,
      });
    }

    return { contactId: contactRef.id };
  } catch (error) {
    console.error("Error enviando contacto a especialista:", error);
    throw new Error(`Failed to contact specialist: ${error.message}`);
  }
};

/**
 * Obtiene artículos de información sobre síndrome de Down con filtros
 * @param {Object} options - Opciones de consulta
 * @param {string} [options.language='es'] - Idioma de los artículos
 * @param {string} [options.category='all'] - Categoría para filtrar
 * @param {string} [options.search=''] - Término de búsqueda
 * @param {number} [options.page=1] - Número de página
 * @param {number} [options.limit=9] - Resultados por página
 * @returns {Promise<{articles: Array, totalArticles: number, totalPages: number}>}
 */
export const getDownSyndromeArticles = async (options = {}) => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    const {
      language = "es",
      category = "all",
      search = "",
      page = 1,
      limit = 9,
    } = options;

    // Construir la referencia a la colección según el idioma
    const collectionName = `down_syndrome_info_${language}`;
    const articlesRef = collection(db, collectionName);

    // Construir la consulta base
    let baseQuery = articlesRef;

    // Aplicar filtro de categoría si no es 'all'
    if (category && category !== "all") {
      baseQuery = query(baseQuery, where("category", "==", category));
    }

    // Obtener el conteo total para la paginación
    const totalSnapshot = await getCountFromServer(baseQuery);
    const totalArticles = totalSnapshot.data().count;

    // Consulta con ordenación y paginación
    let articlesQuery = query(
      baseQuery,
      orderBy("createdAt", "desc"),
      limit(parseInt(limit)),
    );

    // Si hay una página específica, calcular el offset
    if (page > 1) {
      const pageSize = parseInt(limit);
      const snapshot = await getDocs(
        query(
          baseQuery,
          orderBy("createdAt", "desc"),
          limit((page - 1) * pageSize),
        ),
      );

      // Si hay documentos suficientes, usar el último como punto de inicio
      if (snapshot.docs.length > 0) {
        const lastVisible = snapshot.docs[snapshot.docs.length - 1];
        articlesQuery = query(
          baseQuery,
          orderBy("createdAt", "desc"),
          startAfter(lastVisible),
          limit(pageSize),
        );
      }
    }

    // Ejecutar la consulta
    const snapshot = await getDocs(articlesQuery);
    let articles = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || null,
    }));

    // Si hay término de búsqueda, filtrar los resultados en memoria
    if (search && search.trim() !== "") {
      const searchLower = search.toLowerCase();
      articles = articles.filter(
        (article) =>
          article.title?.toLowerCase().includes(searchLower) ||
          article.content?.toLowerCase().includes(searchLower) ||
          article.summary?.toLowerCase().includes(searchLower),
      );
    }

    return {
      articles,
      totalArticles,
      totalPages: Math.ceil(totalArticles / limit),
      currentPage: parseInt(page),
    };
  } catch (error) {
    console.error("Error obteniendo artículos sobre síndrome de Down:", error);
    throw new Error(`Failed to fetch Down syndrome articles: ${error.message}`);
  }
};

/**
 * Obtiene un artículo específico por ID
 * @param {string} articleId - ID del artículo
 * @param {string} [language='es'] - Idioma del artículo
 * @returns {Promise<Object>} - Datos del artículo
 */
export const getDownSyndromeArticleById = async (
  articleId,
  language = "es",
) => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    const collectionName = `down_syndrome_info_${language}`;
    const articleRef = doc(db, collectionName, articleId);
    const articleDoc = await getDoc(articleRef);

    if (!articleDoc.exists()) {
      throw new Error("Artículo no encontrado");
    }

    // Incrementar contador de vistas
    await updateDoc(articleRef, {
      viewCount: increment(1),
    });

    return {
      id: articleDoc.id,
      ...articleDoc.data(),
      createdAt: articleDoc.data().createdAt?.toDate() || null,
      updatedAt: articleDoc.data().updatedAt?.toDate() || null,
    };
  } catch (error) {
    console.error(`Error obteniendo artículo ${articleId}:`, error);
    throw new Error(`Failed to fetch article details: ${error.message}`);
  }
};

/**
 * Obtiene las categorías disponibles para artículos sobre síndrome de Down
 * @param {string} [language='es'] - Idioma para las categorías
 * @returns {Promise<Array<string>>} - Lista de categorías
 */
export const getDownSyndromeCategories = async (language = "es") => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    const categoriesRef = doc(db, "down_syndrome_categories", language);
    const categoriesDoc = await getDoc(categoriesRef);

    if (!categoriesDoc.exists()) {
      // Si no hay documento específico, devolver categorías por defecto
      return [
        "general",
        "tratamientos",
        "educación",
        "familia",
        "investigación",
        "vida_adulta",
        "eventos",
        "recursos",
        "salud_mental",
        "terapias_alternativas",
        "apoyo_legal",
      ];
    }

    return categoriesDoc.data().categories || [];
  } catch (error) {
    console.error(
      `Error obteniendo categorías para idioma ${language}:`,
      error,
    );
    throw new Error(`Failed to fetch article categories: ${error.message}`);
  }
};

/**
 * Obtiene recursos sobre síndrome de Down (asociaciones, centros, etc.)
 * @param {Object} options - Opciones de consulta
 * @param {string} [options.category='all'] - Categoría de recursos
 * @param {string} [options.language='es'] - Idioma de los recursos
 * @param {number} [options.limit=12] - Número de recursos a obtener
 * @returns {Promise<Array>} - Lista de recursos
 */
export const getDownSyndromeResources = async (options = {}) => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    const { category = "all", language = "es", limit = 12 } = options;

    const collectionName = `down_syndrome_resources_${language}`;
    let resourcesQuery = collection(db, collectionName);

    // Aplicar filtro de categoría
    if (category && category !== "all") {
      resourcesQuery = query(resourcesQuery, where("category", "==", category));
    }

    // Ordenar por prioridad y limitar resultados
    resourcesQuery = query(
      resourcesQuery,
      orderBy("priority", "desc"),
      limit(parseInt(limit)),
    );

    const snapshot = await getDocs(resourcesQuery);

    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error("Error obteniendo recursos sobre síndrome de Down:", error);
    throw new Error(
      `Failed to fetch Down syndrome resources: ${error.message}`,
    );
  }
};

/**
 * Obtiene artículos relacionados a un artículo específico
 * @param {string} articleId - ID del artículo de referencia
 * @param {string} [language='es'] - Idioma de los artículos
 * @param {number} [limit=3] - Número de artículos relacionados a obtener
 * @returns {Promise<Array>} - Lista de artículos relacionados
 */
export const getRelatedDownSyndromeArticles = async (
  articleId,
  language = "es",
  limit = 3,
) => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    // Primero obtener la categoría del artículo actual
    const collectionName = `down_syndrome_info_${language}`;
    const articleRef = doc(db, collectionName, articleId);
    const articleDoc = await getDoc(articleRef);

    if (!articleDoc.exists()) {
      throw new Error("Artículo no encontrado");
    }

    const category = articleDoc.data().category || "general";

    // Buscar artículos de la misma categoría, excluyendo el actual
    const relatedQuery = query(
      collection(db, collectionName),
      where("category", "==", category),
      where(documentId(), "!=", articleId),
      orderBy(documentId()),
      limit(parseInt(limit)),
    );

    const snapshot = await getDocs(relatedQuery);

    return snapshot.docs.map((doc) => ({
      id: doc.id,
      title: doc.data().title,
      summary: doc.data().summary,
      category: doc.data().category,
      imageUrl: doc.data().imageUrl,
    }));
  } catch (error) {
    console.error(
      `Error obteniendo artículos relacionados para ${articleId}:`,
      error,
    );
    throw new Error(`Failed to fetch related articles: ${error.message}`);
  }
};

/**
 * Elimina una conversación de chatbot y sus mensajes asociados
 * @param {string} conversationId - ID de la conversación
 * @param {string} userId - ID del usuario
 * @returns {Promise<{success: boolean}>} - Estado de la eliminación
 */
export const deleteChatConversation = async (conversationId, userId) => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    // Verificar permisos
    const conversationRef = doc(
      db,
      COLLECTIONS.CHAT_CONVERSATIONS,
      conversationId,
    );
    const conversationDoc = await getDoc(conversationRef);

    if (!conversationDoc.exists()) {
      throw new Error("Conversación no encontrada");
    }

    if (conversationDoc.data().userId !== userId) {
      throw new Error("No tienes permiso para eliminar esta conversación");
    }

    // Primero eliminar todos los mensajes asociados
    const messagesRef = query(
      collection(db, COLLECTIONS.CHAT_MESSAGES),
      where("conversationId", "==", conversationId),
    );

    const messagesSnapshot = await getDocs(messagesRef);
    const batch = db.batch();

    messagesSnapshot.forEach((doc) => {
      batch.delete(doc.ref);
    });

    // Luego eliminar la conversación
    batch.delete(conversationRef);
    await batch.commit();

    return { success: true };
  } catch (error) {
    console.error("Error al eliminar conversación:", error);
    throw new Error(`Failed to delete conversation: ${error.message}`);
  }
};

export const searchDownSyndromeInfo = async (
  query,
  language = "es",
  limit = 5,
) => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    // Esta es una implementación básica, para una búsqueda más avanzada
    // se recomendaría usar Algolia u otro servicio de búsqueda especializado
    const collectionName = `down_syndrome_info_${language}`;
    const articlesRef = collection(db, collectionName);
    const snapshot = await getDocs(query(articlesRef, limit(20)));

    const searchTerms = query
      .toLowerCase()
      .split(" ")
      .filter((term) => term.length > 2);

    if (searchTerms.length === 0) {
      return [];
    }

    // Búsqueda básica en memoria
    const results = snapshot.docs
      .map((doc) => {
        const data = doc.data();
        let score = 0;

        // Buscar en título, contenido y resumen
        const searchableText = `${data.title || ""} ${data.content || ""} ${
          data.summary || ""
        }`.toLowerCase();

        searchTerms.forEach((term) => {
          if (searchableText.includes(term)) score++;
        });

        return {
          id: doc.id,
          ...data,
          score,
        };
      })
      .filter((item) => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, limit);

    return results;
  } catch (error) {
    console.error("Error en la búsqueda de información:", error);
    throw new Error(`Error en búsqueda: ${error.message}`);
  }
};

export const getVoicePreferences = async (userId) => {
  if (!db || !firebaseStatus.services.firestore || !userId) {
    return { enabled: false, voice: "default", rate: 1.0, pitch: 1.0 };
  }

  try {
    const userRef = doc(db, COLLECTIONS.USERS, userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      return { enabled: false, voice: "default", rate: 1.0, pitch: 1.0 };
    }

    const userData = userDoc.data();
    return {
      enabled: userData.voiceEnabled || false,
      voice: userData.preferredVoice || "default",
      rate: userData.voiceRate || 0.9, // Más lento para mejor comprensión
      pitch: userData.voicePitch || 1.0,
    };
  } catch (error) {
    console.error("Error getting voice preferences:", error);
    return { enabled: false, voice: "default", rate: 1.0, pitch: 1.0 };
  }
};

export const saveVoicePreferences = async (userId, preferences) => {
  if (!db || !firebaseStatus.services.firestore || !userId) {
    throw new Error("Firestore no inicializado o usuario inválido");
  }

  try {
    const userRef = doc(db, COLLECTIONS.USERS, userId);
    await updateDoc(userRef, {
      voiceEnabled: preferences.enabled || false,
      preferredVoice: preferences.voice || "default",
      voiceRate: preferences.rate || 0.9,
      voicePitch: preferences.pitch || 1.0,
      updatedAt: serverTimestamp(),
    });

    return { success: true };
  } catch (error) {
    console.error("Error saving voice preferences:", error);
    throw new Error(`Failed to save preferences: ${error.message}`);
  }
};

/**
 * Obtiene recursos artísticos con filtros y paginación
 * @param {Object} options - Opciones para filtrar y paginar
 * @param {string} [options.category='all'] - Categoría a filtrar
 * @param {boolean} [options.isPaid] - Filtrar por recursos premium/gratuitos
 * @param {Array} [options.ageRange] - Rango de edad [min, max]
 * @param {string} [options.search=''] - Término de búsqueda
 * @param {string} [options.format='all'] - Formato del recurso (pdf, printable, etc)
 * @param {boolean} [options.onlySaved=false] - Solo mostrar recursos guardados
 * @param {string} [options.userId] - ID del usuario (requerido para onlySaved)
 * @param {number} [options.pageSize=12] - Tamaño de página
 * @param {Object} [options.lastDocument=null] - Último documento para paginación
 * @returns {Promise<{data: Array, lastVisible: Object, hasMore: boolean}>}
 */
export const getResourcesArt = async (options = {}) => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    const {
      category = "all",
      isPaid,
      ageRange,
      search = "",
      format = "all",
      onlySaved = false,
      userId,
      pageSize = 12,
      lastDocument = null,
    } = options;

    // Si se solicitan solo guardados, verificar userId
    if (onlySaved && !userId) {
      throw new Error("Se requiere userId para filtrar por recursos guardados");
    }

    // Si buscamos favoritos, primero obtener la lista de IDs
    let savedResourceIds = [];
    if (onlySaved && userId) {
      const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, userId));
      if (userDoc.exists()) {
        savedResourceIds = userDoc.data().saved_resources || [];
      }

      // Si no hay recursos guardados, devolver lista vacía
      if (savedResourceIds.length === 0) {
        return { data: [], lastVisible: null, hasMore: false };
      }
    }

    // Construir la consulta base
    let resourcesQuery = collection(db, COLLECTIONS.RESOURCES_ART);

    // Aplicar filtros
    const filters = [];

    if (category && category !== "all") {
      filters.push({ field: "category", operator: "==", value: category });
    }

    if (isPaid !== undefined) {
      filters.push({ field: "isPaid", operator: "==", value: isPaid });
    }

    if (format && format !== "all") {
      filters.push({ field: "format", operator: "==", value: format });
    }

    // Filtro de edad (si el recurso aplica para cualquier edad dentro del rango)
    if (ageRange && Array.isArray(ageRange) && ageRange.length === 2) {
      if (ageRange[0] > 0) {
        filters.push({ field: "maxAge", operator: ">=", value: ageRange[0] });
      }
      if (ageRange[1] < 18) {
        filters.push({ field: "minAge", operator: "<=", value: ageRange[1] });
      }
    }

    // Para recursos guardados, usamos 'where in' con la lista de IDs
    if (onlySaved && savedResourceIds.length > 0) {
      // Firebase tiene un límite de 10 IDs en una consulta 'in'
      // Si hay más, necesitamos dividir en múltiples consultas
      const chunkSize = 10;
      if (savedResourceIds.length <= chunkSize) {
        filters.push({
          field: documentId(),
          operator: "in",
          value: savedResourceIds,
        });
      } else {
        // Para simplificar, tomamos solo los primeros 10 IDs
        // En una implementación real, habría que hacer múltiples consultas
        const firstChunk = savedResourceIds.slice(0, chunkSize);
        filters.push({
          field: documentId(),
          operator: "in",
          value: firstChunk,
        });
      }
    }

    // Aplicar todos los filtros a la consulta
    let q = resourcesQuery;
    filters.forEach((filter) => {
      q = query(q, where(filter.field, filter.operator, filter.value));
    });

    // Ordenar por fecha de creación descendente
    q = query(q, orderBy("createdAt", "desc"));

    // Paginación
    if (lastDocument) {
      q = query(q, startAfter(lastDocument));
    }

    // Limitar resultados
    q = query(q, limit(pageSize));

    // Ejecutar consulta
    const snapshot = await getDocs(q);

    // Procesar resultados
    let resources = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    // Filtrar por término de búsqueda si existe (client-side)
    if (search && search.trim() !== "") {
      const searchLower = search.toLowerCase();
      resources = resources.filter(
        (resource) =>
          resource.title?.toLowerCase().includes(searchLower) ||
          resource.description?.toLowerCase().includes(searchLower) ||
          resource.tags?.some((tag) => tag.toLowerCase().includes(searchLower)),
      );
    }

    return {
      data: resources,
      lastVisible:
        snapshot.docs.length > 0
          ? snapshot.docs[snapshot.docs.length - 1]
          : null,
      hasMore: snapshot.docs.length >= pageSize,
    };
  } catch (error) {
    console.error("Error getting resources:", error);
    throw new Error(`Failed to fetch resources: ${error.message}`);
  }
};

/**
 * Obtiene detalles de un recurso específico
 * @param {string} resourceId - ID del recurso
 * @returns {Promise<Object>} - Datos del recurso
 */
export const getResourceById = async (resourceId) => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    const resourceRef = doc(db, COLLECTIONS.RESOURCES_ART, resourceId);
    const resourceDoc = await getDoc(resourceRef);

    if (!resourceDoc.exists()) {
      throw new Error("Recurso no encontrado");
    }

    return {
      id: resourceDoc.id,
      ...resourceDoc.data(),
    };
  } catch (error) {
    console.error(`Error getting resource ${resourceId}:`, error);
    throw new Error(`Failed to fetch resource: ${error.message}`);
  }
};

/**
 * Marca/desmarca un recurso como favorito
 * @param {string} userId - ID del usuario
 * @param {string} resourceId - ID del recurso
 * @param {boolean} isSaved - Si debe agregarse o quitarse de favoritos
 * @returns {Promise<{success: boolean}>}
 */
export const toggleSavedResource = async (userId, resourceId, isSaved) => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  if (!userId) {
    throw new Error("Se requiere iniciar sesión");
  }

  const userRef = doc(db, COLLECTIONS.USERS, userId);

  try {
    // Comprobar si el documento del usuario existe antes de actualizar
    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) {
      console.warn(
        `Usuario ${userId} no encontrado al intentar guardar recurso ${resourceId}`,
      );
      throw new Error("Usuario no encontrado.");
    }

    if (isSaved) {
      // --- Eliminar de favoritos ---
      await updateDoc(userRef, {
        // arrayRemove quita el elemento del array
        saved_resources: arrayRemove(resourceId),
      });
      console.log(
        `Recurso ${resourceId} eliminado de favoritos para el usuario ${userId}`,
      );
      return false; // Devuelve el nuevo estado: ya no está guardado
    } else {
      // --- Añadir a favoritos ---
      await updateDoc(userRef, {
        // arrayUnion añade el elemento si no existe ya en el array
        saved_resources: arrayUnion(resourceId),
      });
      console.log(
        `Recurso ${resourceId} añadido a favoritos para el usuario ${userId}`,
      );
      return true; // Devuelve el nuevo estado: ahora está guardado
    }
  } catch (error) {
    console.error(
      `Error al actualizar recurso guardado ${resourceId} para el usuario ${userId}:`,
      error,
    );
    throw new Error(
      "No se pudo actualizar tu lista de recursos guardados. Inténtalo de nuevo.",
    );
  }
};

/**
 * Verifica si un recurso está en la lista de favoritos
 * @param {string} userId - ID del usuario
 * @param {string} resourceId - ID del recurso
 * @returns {Promise<boolean>} - true si está guardado
 */
export const isResourceSaved = async (userId, resourceId) => {
  if (!db || !firebaseStatus.services.firestore || !userId) {
    return false;
  }

  try {
    const userRef = doc(db, COLLECTIONS.USERS, userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      return false;
    }

    const savedResources = userDoc.data().saved_resources || [];
    return savedResources.includes(resourceId);
  } catch (error) {
    console.error("Error checking saved resource:", error);
    return false;
  }
};

/**
 * Obtiene los créditos disponibles de un usuario
 * @param {string} userId - ID del usuario
 * @returns {Promise<number>} - Cantidad de créditos
 */
export const getUserCredits = async (userId) => {
  if (!db || !firebaseStatus.services.firestore || !userId) {
    throw new Error("Firestore no inicializado o usuario inválido");
  }

  try {
    const userRef = doc(db, COLLECTIONS.USERS, userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      throw new Error("Usuario no encontrado");
    }

    return userDoc.data().credits || 0;
  } catch (error) {
    console.error(
      `Error obteniendo créditos para el usuario ${userId}:`,
      error,
    );
    throw new Error(`Failed to get credits: ${error.message}`);
  }
};

/**
 * Obtiene la URL de descarga de un recurso y registra la descarga.
 * IMPORTANTE: La verificación y consumo de créditos DEBE realizarse en el backend
 * ANTES de llamar a esta función o de proporcionar la URL al usuario.
 *
 * @param {string} userId - ID del usuario que descarga
 * @param {string} resourceId - ID del recurso a descargar
 * @returns {Promise<{success: boolean, downloadUrl: string}>} - URL de descarga
 */
export const downloadResource = async (userId, resourceId) => {
  // Verificar inicialización y parámetros
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }
  if (!resourceId) {
    throw new Error("Se requiere el ID del recurso");
  }

  try {
    // 1. Obtener información del recurso
    const resourceRef = doc(db, COLLECTIONS.RESOURCES_ART, resourceId);
    const resourceDoc = await getDoc(resourceRef);

    if (!resourceDoc.exists()) {
      throw new Error("Recurso no encontrado");
    }

    const resourceData = resourceDoc.data();

    // 2. Obtener la URL de descarga
    let downloadUrl = resourceData.fileUrl;

    // Si no hay URL directa pero hay una ubicación de archivo en Storage
    if (!downloadUrl && resourceData.filePath) {
      if (!storage) {
        throw new Error("Firebase Storage no inicializado");
      }
      try {
        const fileRef = ref(storage, resourceData.filePath);
        downloadUrl = await getDownloadURL(fileRef);
      } catch (storageError) {
        console.error(
          `Error obteniendo URL de Storage para ${resourceData.filePath}:`,
          storageError,
        );
        throw new Error("No se pudo obtener la URL de descarga del archivo.");
      }
    }

    // Si después de intentar ambas formas no hay URL, lanzar error
    if (!downloadUrl) {
      console.error(
        `No se encontró fileUrl ni filePath válido para el recurso ${resourceId}`,
      );
      throw new Error(
        "No se pudo determinar la URL de descarga para este recurso.",
      );
    }

    // 3. Registrar la descarga (sin lógica de créditos)
    // Usar setDoc con un ID predecible para evitar duplicados si se llama rápido
    const downloadDocId = `${userId}_${resourceId}`;
    const downloadRef = doc(db, COLLECTIONS.RESOURCE_DOWNLOADS, downloadDocId);

    // Usar set con merge para no sobrescribir si ya existe, solo actualizar timestamp
    await setDoc(
      downloadRef,
      {
        userId,
        resourceId,
        timestamp: serverTimestamp(),
        isPremium: resourceData.isPremium || false, // Guardar si era premium para análisis
        resourceTitle: resourceData.title || "Recurso sin título", // Guardar título para referencia
      },
      { merge: true },
    ); // Usar merge para actualizar timestamp si ya existe

    // 4. Incrementar contador de descargas del recurso (opcional, pero útil)
    // Se hace de forma asíncrona y no crítica para no bloquear la descarga
    updateDoc(resourceRef, {
      downloads: increment(1),
    }).catch((err) =>
      console.warn(
        `Error incrementando contador de descargas para ${resourceId}:`,
        err,
      ),
    );

    // 5. Devolver éxito y la URL
    console.log(
      `Descarga registrada para usuario ${userId}, recurso ${resourceId}. URL obtenida.`,
    );
    return {
      success: true,
      downloadUrl,
    };
  } catch (error) {
    // Loguear el error específico antes de lanzar uno genérico
    console.error(
      `Error en downloadResource para usuario ${userId}, recurso ${resourceId}:`,
      error,
    );
    // Devolver un mensaje de error más genérico al usuario final
    throw new Error(
      `Error al procesar la descarga del recurso: ${error.message}`,
    );
  }
};

/**
 * Registra una descarga de recurso y consume crédito si es premium
 * @param {string} userId - ID del usuario
 * @param {string} resourceId - ID del recurso
 * @returns {Promise<{success: boolean, downloadUrl: string}>}
 */
export const downloadResourceOld = async (userId, resourceId) => {
  if (!db || !firebaseStatus.services.firestore || !userId) {
    throw new Error("Firestore no inicializado o usuario inválido");
  }

  try {
    // Obtener información del recurso
    const resourceRef = doc(db, COLLECTIONS.RESOURCES_ART, resourceId);
    const resourceDoc = await getDoc(resourceRef);

    if (!resourceDoc.exists()) {
      throw new Error("Recurso no encontrado");
    }

    const resourceData = resourceDoc.data();
    const isPremium = resourceData.isPremium || false;

    // Verificar si ya se descargó este recurso antes (para evitar cobrar crédito dos veces)
    const previousDownloadsQuery = query(
      collection(db, COLLECTIONS.RESOURCE_DOWNLOADS),
      where("userId", "==", userId),
      where("resourceId", "==", resourceId),
      limit(1),
    );

    const previousDownloads = await getDocs(previousDownloadsQuery);
    const alreadyDownloaded = !previousDownloads.empty;

    // Si es premium y no se descargó antes, verificar y consumir crédito
    if (isPremium && !alreadyDownloaded) {
      const userRef = doc(db, COLLECTIONS.USERS, userId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        throw new Error("Usuario no encontrado");
      }

      const credits = userDoc.data().credits || 0;

      if (credits < 1) {
        throw new Error(
          "No tienes suficientes créditos para descargar este recurso",
        );
      }

      // Consumir un crédito (transacción atómica)
      await updateDoc(userRef, {
        credits: increment(-1),
      });

      // Registrar la transacción
      await addDoc(collection(db, COLLECTIONS.CREDIT_TRANSACTIONS), {
        userId,
        resourceId,
        type: "download",
        amount: -1,
        timestamp: serverTimestamp(),
        resourceTitle: resourceData.title || "Recurso sin título",
      });
    }

    // Registrar la descarga
    await addDoc(collection(db, COLLECTIONS.RESOURCE_DOWNLOADS), {
      userId,
      resourceId,
      timestamp: serverTimestamp(),
      isPremium,
    });

    // Incrementar contador de descargas en el recurso
    await updateDoc(resourceRef, {
      downloads: increment(1),
    });

    // Generar URL de descarga (podría ser temporal si es necesario)
    let downloadUrl = resourceData.fileUrl;

    // Si no hay URL directa pero hay una ubicación de archivo en Storage
    if (!downloadUrl && resourceData.filePath) {
      const fileRef = ref(storage, resourceData.filePath);
      downloadUrl = await getDownloadURL(fileRef);
    }

    if (!downloadUrl) {
      throw new Error("No se pudo obtener la URL de descarga");
    }

    return {
      success: true,
      isPremium,
      downloadUrl,
      alreadyDownloaded,
    };
  } catch (error) {
    console.error(`Error downloading resource ${resourceId}:`, error);
    throw new Error(`Error al descargar recurso: ${error.message}`);
  }
};

/**
 * Obtiene categorías de recursos artísticos
 * @returns {Promise<Array>} - Lista de categorías
 */
export const getResourceCategories = async () => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    const categoriesRef = collection(db, COLLECTIONS.RESOURCE_CATEGORIES);
    const categoriesQuery = query(categoriesRef, orderBy("order", "asc"));
    const snapshot = await getDocs(categoriesQuery);

    if (snapshot.empty) {
      // Categorías predeterminadas si no hay configuradas en la DB
      return [
        { id: "family", name: "Familia", icon: "FamilyRestroom" },
        { id: "education", name: "Educación", icon: "School" },
        { id: "health", name: "Salud", icon: "LocalHospital" },
        {
          id: "individuals",
          name: "Personas con síndrome de Down",
          icon: "EmojiPeople",
        },
        { id: "creative", name: "Creativos", icon: "Palette" },
        { id: "literature", name: "Literatura", icon: "MenuBook" },
      ];
    }

    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error("Error fetching resource categories:", error);
    throw new Error(`Failed to fetch categories: ${error.message}`);
  }
};

/**
 * Envía una entrada para el concurso
 * @param {Object} entryData - Datos de la entrada
 * @param {string} entryData.userId - ID del usuario
 * @param {string} entryData.title - Título de la entrada
 * @param {string} entryData.description - Descripción de la entrada
 * @param {File} [entryData.file] - Archivo adjunto (opcional)
 * @returns {Promise<{entryId: string}>} - ID de la entrada creada
 */
export const submitContestEntry = async (entryData) => {
  if (!db || !firebaseStatus.services.firestore || !storage) {
    throw new Error("Firebase not initialized");
  }

  try {
    const { userId, title, description, file } = entryData;

    // Validaciones básicas
    if (!title || title.trim().length < 3) {
      throw new Error("El título debe tener al menos 3 caracteres");
    }

    if (!description || description.trim().length < 10) {
      throw new Error("La descripción debe tener al menos 10 caracteres");
    }

    if (!userId) {
      throw new Error("Debes iniciar sesión para participar");
    }

    // Subir archivo si existe
    let fileUrl = null;
    let fileType = null;

    if (file) {
      // Validar tamaño (5MB máx)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error("El archivo no puede superar los 5MB");
      }

      // Validar tipo
      const validTypes = [
        "image/jpeg",
        "image/png",
        "image/gif",
        "application/pdf",
      ];
      if (!validTypes.includes(file.type)) {
        throw new Error("Solo se permiten imágenes (JPEG, PNG, GIF) y PDF");
      }

      // Generar nombre único
      const fileExt = file.name.split(".").pop();
      const fileName = `${userId}_${Date.now()}.${fileExt}`;
      const filePath = `${STORAGE_PATHS.CONTEST_UPLOADS}/${fileName}`;

      // Subir a Storage
      const fileRef = ref(storage, filePath);
      await uploadBytesResumable(fileRef, file);

      // Obtener URL de descarga
      fileUrl = await getDownloadURL(fileRef);
      fileType = file.type;
    }

    // Obtener nombre de usuario
    const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, userId));
    const userName = userDoc.exists()
      ? userDoc.data().displayName || userDoc.data().username || "Usuario"
      : "Usuario";

    // Guardar entrada en Firestore
    const entryRef = doc(collection(db, COLLECTIONS.CONTEST_ENTRIES));
    await setDoc(entryRef, {
      userId,
      userName,
      title: title.trim(),
      description: description.trim(),
      fileUrl,
      fileType,
      createdAt: serverTimestamp(),
      status: "pending", // pending, approved, rejected
      votes: 0,
    });

    return { entryId: entryRef.id };
  } catch (error) {
    console.error("Error submitting contest entry:", error);
    throw new Error(`Failed to submit entry: ${error.message}`);
  }
};

/**
 * Obtiene entradas aprobadas del concurso
 * @param {Object} options - Opciones de consulta
 * @param {number} [options.limit=10] - Número de entradas a obtener
 * @returns {Promise<Array>} - Lista de entradas
 */
export const getContestEntries = async (options = {}) => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    const { limit = 10 } = options;

    const entriesRef = collection(db, COLLECTIONS.CONTEST_ENTRIES);
    const entriesQuery = query(
      entriesRef,
      where("status", "==", "approved"),
      orderBy("createdAt", "desc"),
      limit(parseInt(limit)),
    );

    const snapshot = await getDocs(entriesQuery);

    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
    }));
  } catch (error) {
    console.error("Error fetching contest entries:", error);
    throw new Error(`Failed to fetch entries: ${error.message}`);
  }
};

/**
 * Añade comentarios a un recurso artístico
 * @param {Object} commentData - Datos del comentario
 * @param {string} commentData.userId - ID del usuario
 * @param {string} commentData.resourceId - ID del recurso
 * @param {string} commentData.text - Contenido del comentario
 * @returns {Promise<{commentId: string}>}
 */
export const addResourceComment = async (commentData) => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    const { userId, resourceId, text } = commentData;

    // Validaciones básicas
    if (!text || text.trim().length < 2) {
      throw new Error("El comentario debe tener al menos 2 caracteres");
    }

    if (!userId) {
      throw new Error("Debes iniciar sesión para comentar");
    }

    // Obtener información del usuario
    let userData = { role: "user" }; // Valor por defecto
    let authorName = "Usuario";
    let authorPhoto = null;
    const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, userId));
    if (userDoc.exists()) {
      userData = userDoc.data();
      authorName = userData.displayName || userData.username || "Usuario";
      authorPhoto = userData.photoURL;
    }

    // Crear comentario
    const commentRef = doc(collection(db, COLLECTIONS.RESOURCE_COMMENTS));
    await setDoc(commentRef, {
      userId,
      resourceId,
      userName: authorName,
      userPhoto: authorPhoto,
      text: text.trim(),
      createdAt: serverTimestamp(),
      approved: userData.role === "admin" || userData.role === "moderator",
    });

    // Incrementar contador de comentarios en el recurso
    const resourceRef = doc(db, COLLECTIONS.RESOURCES_ART, resourceId);
    await updateDoc(resourceRef, {
      commentCount: increment(1),
    });

    return { commentId: commentRef.id };
  } catch (error) {
    console.error("Error adding resource comment:", error);
    throw new Error(`Failed to add comment: ${error.message}`);
  }
};

/**
 * Obtiene comentarios de un recurso con paginación
 * @param {string} resourceId - ID del recurso
 * @param {Object} options - Opciones de paginación
 * @returns {Promise<{comments: Array, lastVisible: Object, hasMore: boolean}>}
 */
export const getResourceComments = async (resourceId, options = {}) => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    const { limit = 10, lastDocument = null } = options;

    // Consulta base
    let commentsQuery = query(
      collection(db, COLLECTIONS.RESOURCE_COMMENTS),
      where("resourceId", "==", resourceId),
      where("approved", "==", true),
      orderBy("createdAt", "desc"),
    );

    // Paginación
    if (lastDocument) {
      commentsQuery = query(commentsQuery, startAfter(lastDocument));
    }

    // Limite
    commentsQuery = query(commentsQuery, limit(parseInt(limit)));

    const snapshot = await getDocs(commentsQuery);

    const comments = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
    }));

    return {
      comments,
      lastVisible:
        snapshot.docs.length > 0
          ? snapshot.docs[snapshot.docs.length - 1]
          : null,
      hasMore: snapshot.docs.length >= limit,
    };
  } catch (error) {
    console.error(`Error getting comments for resource ${resourceId}:`, error);
    throw new Error(`Failed to fetch comments: ${error.message}`);
  }
};

/**
 * Añade un comentario a un recurso artístico
 * @param {Object} commentData - Datos del comentario
 * @param {string} commentData.userId - ID del usuario
 * @param {string} commentData.resourceId - ID del recurso
 * @param {string} commentData.text - Contenido del comentario
 * @returns {Promise<{commentId: string}>} - ID del comentario creado
 */
export const addResourceCommentOld = async (commentData) => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    const { userId, resourceId, text } = commentData;

    // Validación básica
    if (!text || text.trim().length < 2) {
      throw new Error("El comentario debe tener al menos 2 caracteres");
    }

    // Obtener información del usuario
    let userData = {};
    try {
      const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, userId));
      if (userDoc.exists()) {
        userData = userDoc.data();
      }
    } catch (error) {
      console.error(`Error getting user data for ${userId}:`, error);
    }

    // Crear el comentario
    const commentRef = doc(collection(db, COLLECTIONS.RESOURCE_COMMENTS));
    await setDoc(commentRef, {
      resourceId,
      userId,
      text: text.trim(),
      authorName: userData.displayName || userData.username || "Usuario",
      authorPhoto: userData.photoURL || null,
      timestamp: serverTimestamp(),
      approved: true,
    });

    // Incrementar contador de comentarios en el recurso
    const resourceRef = doc(db, COLLECTIONS.RESOURCES_ART, resourceId);
    await updateDoc(resourceRef, {
      commentCount: increment(1),
    });

    return { commentId: commentRef.id };
  } catch (error) {
    console.error("Error adding resource comment:", error);
    throw new Error(`Failed to add comment: ${error.message}`);
  }
};

/**
 * Elimina un comentario de un recurso artístico
 * @param {string} commentId - ID del comentario
 * @returns {Promise<boolean>} - true si se eliminó correctamente
 */
export const deleteResourceComment = async (commentId) => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    // Obtener el comentario
    const commentRef = doc(db, COLLECTIONS.RESOURCE_COMMENTS, commentId);
    const commentDoc = await getDoc(commentRef);

    if (!commentDoc.exists()) {
      throw new Error("Comentario no encontrado");
    }

    // Eliminar el comentario
    await deleteDoc(commentRef);

    // Decrementar contador en el recurso
    const resourceRef = doc(
      db,
      COLLECTIONS.RESOURCES_ART,
      commentDoc.data().resourceId,
    );
    await updateDoc(resourceRef, {
      commentCount: increment(-1),
    });

    return true;
  } catch (error) {
    console.error(`Error deleting resource comment ${commentId}:`, error);
    throw new Error(`Failed to delete comment: ${error.message}`);
  }
};

/**
 * Inicializa las colecciones necesarias para el sistema de recursos
 * @returns {Promise<void>}
 */
export const initResourcesCollections = async () => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  try {
    // Esta función se puede llamar en la inicialización de la app
    // para crear documentos iniciales que garanticen la existencia de colecciones

    // 1. Verificar categorías de recursos
    const categoriesRef = collection(db, COLLECTIONS.RESOURCE_CATEGORIES);
    const categoriesSnapshot = await getDocs(categoriesRef);

    if (categoriesSnapshot.empty) {
      // Crear categorías predeterminadas
      const defaultCategories = [
        { id: "family", name: "Familia", icon: "FamilyRestroom", order: 1 },
        { id: "education", name: "Educación", icon: "School", order: 2 },
        { id: "health", name: "Salud", icon: "LocalHospital", order: 3 },
        {
          id: "individuals",
          name: "Personas con síndrome de Down",
          icon: "EmojiPeople",
          order: 4,
        },
        { id: "creative", name: "Creativos", icon: "Palette", order: 5 },
        { id: "literature", name: "Literatura", icon: "MenuBook", order: 6 },
      ];

      // Crear operaciones en batch
      const batch = writeBatch(db);

      defaultCategories.forEach((category) => {
        const docRef = doc(db, COLLECTIONS.RESOURCE_CATEGORIES, category.id);
        batch.set(docRef, {
          name: category.name,
          icon: category.icon,
          order: category.order,
          createdAt: serverTimestamp(),
        });
      });

      await batch.commit();
      console.log("Se han inicializado las categorías de recursos");
    }

    // Se podrían inicializar más colecciones según sea necesario
  } catch (error) {
    console.error("Error initializing resources collections:", error);
    // No lanzamos error para evitar que falle la inicialización de la app
  }
};

/**
 * Obtiene la configuración para servicios externos como pasarelas de pago
 * @returns {Promise<Object>} La configuración de la aplicación
 */
export const getConfig = async () => {
  try {
    const configDoc = await getDoc(doc(db, "app_config", "payment"));
    if (configDoc.exists()) {
      return configDoc.data();
    }
    return {
      stripe: {
        publicKey: process.env.REACT_APP_STRIPE_PUBLIC_KEY || "",
      },
      paypal: {
        clientId: process.env.REACT_APP_PAYPAL_CLIENT_ID || "",
      },
    };
  } catch (error) {
    console.error("Error obteniendo configuración:", error);
    return {};
  }
};

/**
 * Verifica si un especialista está guardado por un usuario
 * @param {string} userId - ID del usuario
 * @param {string} specialistId - ID del especialista
 * @returns {Promise<boolean>} - true si está guardado, false si no o en caso de error/no logueado
 */
export const isSpecialistSaved = async (userId, specialistId) => {
  if (!db || !firebaseStatus.services.firestore || !userId || !specialistId) {
    return false; // Validaciones básicas
  }

  try {
    const userRef = doc(db, COLLECTIONS.USERS, userId);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      const data = userDoc.data();
      // Comprueba si el array existe y si incluye el ID
      return (
        Array.isArray(data.savedSpecialists) &&
        data.savedSpecialists.includes(specialistId)
      );
    }
    return false; // Usuario no encontrado
  } catch (error) {
    console.error(
      `Error checking if specialist ${specialistId} is saved by user ${userId}:`,
      error,
    );
    return false; // Asumir que no está guardado si hay error
  }
};

/**
 * Guarda o elimina un especialista de la lista de favoritos de un usuario
 * Actualiza el campo 'savedSpecialists' (array) en el documento del usuario
 * @param {string} userId - ID del usuario
 * @param {string} specialistId - ID del especialista
 * @param {boolean} currentlySaved - Indica si el especialista está actualmente guardado
 * @returns {Promise<boolean>} - El nuevo estado de guardado (true si ahora está guardado, false si no)
 * @throws {Error} Si no se puede actualizar Firestore
 */
export const toggleSavedSpecialist = async (
  userId,
  specialistId,
  currentlySaved,
) => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }

  if (!userId || !specialistId) {
    throw new Error("Se requiere iniciar sesión");
  }

  const userRef = doc(db, COLLECTIONS.USERS, userId);

  try {
    // Comprobar si el documento del usuario existe antes de actualizar
    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) {
      console.warn(
        `Usuario ${userId} no encontrado al intentar guardar especialista ${specialistId}`,
      );
      throw new Error("Usuario no encontrado.");
    }

    if (currentlySaved) {
      // --- Eliminar de favoritos ---
      await updateDoc(userRef, {
        // arrayRemove quita el elemento del array
        savedSpecialists: arrayRemove(specialistId),
      });
      console.log(
        `Specialist ${specialistId} removed from favorites for user ${userId}`,
      );
      return false; // Devuelve el nuevo estado: ya no está guardado
    } else {
      // --- Añadir a favoritos ---
      await updateDoc(userRef, {
        // arrayUnion añade el elemento si no existe ya en el array
        savedSpecialists: arrayUnion(specialistId),
      });
      console.log(
        `Specialist ${specialistId} added to favorites for user ${userId}`,
      );
      return true; // Devuelve el nuevo estado: ahora está guardado
    }
  } catch (error) {
    console.error(
      `Error toggling saved specialist ${specialistId} for user ${userId}:`,
      error,
    );
    throw new Error(
      "No se pudo actualizar tu lista de especialistas guardados. Inténtalo de nuevo.",
    );
  }
};

/**
 * Obtiene comentarios hechos por un usuario en recursos artísticos con paginación y títulos de recursos.
 * @param {string} userId - ID del usuario.
 * @param {number} [limitPerPage=10] - Número de comentarios por página.
 * @param {Object} [lastDocCursor=null] - Último documento de la página anterior para paginación.
 * @returns {Promise<{comments: Array, lastVisible: Object, hasMore: boolean}>}
 */
export const getUserResourceComments = async (
  userId,
  limitPerPage = 10,
  lastDocCursor = null,
) => {
  if (!db || !firebaseStatus.services.firestore) {
    throw new Error("Firestore not initialized");
  }
  if (!userId) {
    throw new Error("User ID is required");
  }

  try {
    const commentsRef = collection(db, COLLECTIONS.RESOURCE_COMMENTS); // Usar constante
    const qConstraints = [
      where("userId", "==", userId),
      orderBy("createdAt", "desc"), // Asume campo 'createdAt'
      limit(limitPerPage),
    ];

    if (lastDocCursor) {
      qConstraints.push(startAfter(lastDocCursor));
    }

    const q = query(commentsRef, ...qConstraints);
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return { comments: [], lastVisible: null, hasMore: false };
    }

    // Obtener IDs de recursos para buscar títulos
    const resourceIds = [
      ...new Set(
        querySnapshot.docs.map((d) => d.data().resourceId).filter(Boolean),
      ),
    ];
    const resourceTitlesMap = new Map(); // Usar Map para eficiencia

    if (resourceIds.length > 0) {
      const idChunks = [];
      for (let i = 0; i < resourceIds.length; i += 10) {
        // Límite 'in' es 10
        idChunks.push(resourceIds.slice(i, i + 10));
      }
      try {
        await Promise.all(
          idChunks.map(async (chunk) => {
            if (chunk.length > 0) {
              const resourceQuery = query(
                collection(db, COLLECTIONS.RESOURCES_ART),
                where(documentId(), "in", chunk),
              );
              const resourceSnap = await getDocs(resourceQuery);
              resourceSnap.forEach((doc) => {
                resourceTitlesMap.set(doc.id, doc.data()?.title); // Guardar título en Map
              });
            }
          }),
        );
      } catch (resourceError) {
        console.warn(
          "Error fetching some resource titles for comments:",
          resourceError,
        );
        // Continuar sin títulos para esos recursos si falla
      }
    }

    // Mapear comentarios y añadir títulos
    const comments = querySnapshot.docs.map((docSnap) => {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        resourceTitle:
          resourceTitlesMap.get(data.resourceId) || "Recurso Desconocido", // Obtener título del Map
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : null, // Convertir Timestamp
      };
    });

    const newLastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];
    const hasMore = comments.length === limitPerPage;

    return {
      comments,
      lastVisible: newLastVisible || null,
      hasMore,
    };
  } catch (error) {
    console.error(`Error getting resource comments for user ${userId}:`, error);
    // Podrías querer lanzar un error más específico o traducido aquí
    throw new Error(`Failed to fetch resource comments: ${error.message}`);
  }
};

export { app, auth, db, firebaseAnalytics, functions, storage };
export const analytics = firebaseAnalytics;
